import React, { useState, useEffect, useRef } from "react";
import {
  FaFileInvoice,
  FaFileExcel,
  FaPlus,
  FaUpload,
  FaDownload,
  FaSearch,
  FaTimes,
  FaCheckCircle,
  FaClock,
  FaPaperPlane,
  FaFilter,
  FaCalendarAlt,
  FaUndo,
  FaChevronDown,
  FaChevronUp,
  FaEllipsisV,
  FaEye,
  FaSyncAlt,
  FaCheck,
  FaTrash,
  FaSpinner,
  FaInfoCircle,
  FaPenNib,
  FaFileArchive,
  FaFilePdf,
  FaTimesCircle,
  FaQrcode,
} from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ReactDOMServer from "react-dom/server";
import QRCode from "qrcode";
import apiEndpoints from "../../apiendpoints.json";
import { getAuthHeaders, handleAuthError } from "../../utils/auth";

const BASE_URL = apiEndpoints.equityUrl || "http://localhost:5217";

const getDynamicUpiQrSrc = (upiUri) => {
  if (!upiUri) return "/images/qr.png";
  try {
    if (typeof document !== "undefined") {
      const qr = QRCode.create(upiUri, { errorCorrectionLevel: "M" });
      const size = qr.modules.size;
      const canvas = document.createElement("canvas");
      const scale = 5;
      const margin = 1;
      const canvasSize = (size + margin * 2) * scale;
      canvas.width = canvasSize;
      canvas.height = canvasSize;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasSize, canvasSize);
        ctx.fillStyle = "#000000";
        for (let r = 0; r < size; r++) {
          for (let c = 0; c < size; c++) {
            if (qr.modules.get(r, c)) {
              ctx.fillRect((c + margin) * scale, (r + margin) * scale, scale, scale);
            }
          }
        }
        return canvas.toDataURL("image/png");
      }
    }
  } catch (err) {
    console.warn("Canvas QR generation fallback:", err);
  }

  try {
    const qr = QRCode.create(upiUri, { errorCorrectionLevel: "M" });
    const size = qr.modules.size;
    let path = "";
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (qr.modules.get(r, c)) {
          path += `M${c + 1} ${r + 1}h1v1h-1z `;
        }
      }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size + 2} ${size + 2}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#ffffff"/><path fill="#000000" d="${path.trim()}"/></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  } catch (e) {
    return "/images/qr.png";
  }
};

const formatDateStr = (d) => {
  if (!d) return "";
  if (typeof d === "string") {
    const match = d.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const dateObj = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dateObj.getTime())) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseExcelDate = (val) => {
  if (!val) return "";
  if (val instanceof Date && !isNaN(val.getTime())) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, "0");
    const day = String(val.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  if (typeof val === "number") {
    const d = new Date((val - 25569) * 86400 * 1000);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return "";
    const parts = trimmed.split(/[-/]/);
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
      } else if (parts[2].length === 4) {
        return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    }
  }
  return "";
};
const INDIAN_CITY_STATE_MAP = {
  // ----------------------------------------------------
  // 01: JAMMU AND KASHMIR
  // ----------------------------------------------------
  SRINAGAR: { state: "Jammu and Kashmir", code: "01" },
  JAMMU: { state: "Jammu and Kashmir", code: "01" },
  ANANTNAG: { state: "Jammu and Kashmir", code: "01" },
  BARAMULLA: { state: "Jammu and Kashmir", code: "01" },
  KATHUA: { state: "Jammu and Kashmir", code: "01" },
  UDHAMPUR: { state: "Jammu and Kashmir", code: "01" },

  // ----------------------------------------------------
  // 02: HIMACHAL PRADESH
  // ----------------------------------------------------
  SHIMLA: { state: "Himachal Pradesh", code: "02" },
  DHARAMSHALA: { state: "Himachal Pradesh", code: "02" },
  SOLAN: { state: "Himachal Pradesh", code: "02" },
  MANDI: { state: "Himachal Pradesh", code: "02" },
  KULLU: { state: "Himachal Pradesh", code: "02" },
  MANALI: { state: "Himachal Pradesh", code: "02" },
  BADDI: { state: "Himachal Pradesh", code: "02" },

  // ----------------------------------------------------
  // 03: PUNJAB
  // ----------------------------------------------------
  LUDHIANA: { state: "Punjab", code: "03" },
  AMRITSAR: { state: "Punjab", code: "03" },
  JALANDHAR: { state: "Punjab", code: "03" },
  PATIALA: { state: "Punjab", code: "03" },
  BATHINDA: { state: "Punjab", code: "03" },
  MOHALI: { state: "Punjab", code: "03" },
  "SAS NAGAR": { state: "Punjab", code: "03" },
  PATHANKOT: { state: "Punjab", code: "03" },
  HOSHIARPUR: { state: "Punjab", code: "03" },

  // ----------------------------------------------------
  // 04: CHANDIGARH (UT)
  // ----------------------------------------------------
  CHANDIGARH: { state: "Chandigarh", code: "04" },

  // ----------------------------------------------------
  // 05: UTTARAKHAND
  // ----------------------------------------------------
  DEHRADUN: { state: "Uttarakhand", code: "05" },
  HARIDWAR: { state: "Uttarakhand", code: "05" },
  ROORKEE: { state: "Uttarakhand", code: "05" },
  HALDWANI: { state: "Uttarakhand", code: "05" },
  RUDRAPUR: { state: "Uttarakhand", code: "05" },
  RISHIKESH: { state: "Uttarakhand", code: "05" },
  NAINITAL: { state: "Uttarakhand", code: "05" },

  // ----------------------------------------------------
  // 06: HARYANA
  // ----------------------------------------------------
  GURUGRAM: { state: "Haryana", code: "06" },
  GURGAON: { state: "Haryana", code: "06" },
  FARIDABAD: { state: "Haryana", code: "06" },
  PANIPAT: { state: "Haryana", code: "06" },
  AMBALA: { state: "Haryana", code: "06" },
  KARNAL: { state: "Haryana", code: "06" },
  ROHTAK: { state: "Haryana", code: "06" },
  HISAR: { state: "Haryana", code: "06" },
  SONIPAT: { state: "Haryana", code: "06" },
  PANCHKULA: { state: "Haryana", code: "06" },

  // ----------------------------------------------------
  // 07: DELHI (NCT)
  // ----------------------------------------------------
  DELHI: { state: "Delhi", code: "07" },
  "NEW DELHI": { state: "Delhi", code: "07" },
  "NORTH DELHI": { state: "Delhi", code: "07" },
  "SOUTH DELHI": { state: "Delhi", code: "07" },
  "WEST DELHI": { state: "Delhi", code: "07" },
  "EAST DELHI": { state: "Delhi", code: "07" },

  // ----------------------------------------------------
  // 08: RAJASTHAN
  // ----------------------------------------------------
  JAIPUR: { state: "Rajasthan", code: "08" },
  JODHPUR: { state: "Rajasthan", code: "08" },
  KOTA: { state: "Rajasthan", code: "08" },
  BIKANER: { state: "Rajasthan", code: "08" },
  AJMER: { state: "Rajasthan", code: "08" },
  UDAIPUR: { state: "Rajasthan", code: "08" },
  BHILWARA: { state: "Rajasthan", code: "08" },
  ALWAR: { state: "Rajasthan", code: "08" },
  SIKAR: { state: "Rajasthan", code: "08" },
  BHARATPUR: { state: "Rajasthan", code: "08" },
  PALI: { state: "Rajasthan", code: "08" },

  // ----------------------------------------------------
  // 09: UTTAR PRADESH
  // ----------------------------------------------------
  LUCKNOW: { state: "Uttar Pradesh", code: "09" },
  KANPUR: { state: "Uttar Pradesh", code: "09" },
  GHAZIABAD: { state: "Uttar Pradesh", code: "09" },
  AGRA: { state: "Uttar Pradesh", code: "09" },
  MEERUT: { state: "Uttar Pradesh", code: "09" },
  VARANASI: { state: "Uttar Pradesh", code: "09" },
  PRAYAGRAJ: { state: "Uttar Pradesh", code: "09" },
  ALLAHABAD: { state: "Uttar Pradesh", code: "09" },
  BAREILLY: { state: "Uttar Pradesh", code: "09" },
  ALIGARH: { state: "Uttar Pradesh", code: "09" },
  MORADABAD: { state: "Uttar Pradesh", code: "09" },
  SAHARANPUR: { state: "Uttar Pradesh", code: "09" },
  GORAKHPUR: { state: "Uttar Pradesh", code: "09" },
  NOIDA: { state: "Uttar Pradesh", code: "09" },
  "GREATER NOIDA": { state: "Uttar Pradesh", code: "09" },
  FIROZABAD: { state: "Uttar Pradesh", code: "09" },
  JHANSI: { state: "Uttar Pradesh", code: "09" },
  MATHURA: { state: "Uttar Pradesh", code: "09" },
  AYODHYA: { state: "Uttar Pradesh", code: "09" },
  FAIZABAD: { state: "Uttar Pradesh", code: "09" },
  RAIBARELI: { state: "Uttar Pradesh", code: "09" },

  // ----------------------------------------------------
  // 10: BIHAR
  // ----------------------------------------------------
  PATNA: { state: "Bihar", code: "10" },
  GAYA: { state: "Bihar", code: "10" },
  BHAGALPUR: { state: "Bihar", code: "10" },
  MUZAFFARPUR: { state: "Bihar", code: "10" },
  PURNEA: { state: "Bihar", code: "10" },
  DARBHANGA: { state: "Bihar", code: "10" },
  BIHAR_SHARIF: { state: "Bihar", code: "10" },
  ARRAH: { state: "Bihar", code: "10" },
  BEGUSARAI: { state: "Bihar", code: "10" },
  KATIHAR: { state: "Bihar", code: "10" },

  // ----------------------------------------------------
  // 11: SIKKIM
  // ----------------------------------------------------
  GANGTOK: { state: "Sikkim", code: "11" },
  NAMCHI: { state: "Sikkim", code: "11" },
  GEZING: { state: "Sikkim", code: "11" },
  MANGAN: { state: "Sikkim", code: "11" },

  // ----------------------------------------------------
  // 12: ARUNACHAL PRADESH
  // ----------------------------------------------------
  ITANAGAR: { state: "Arunachal Pradesh", code: "12" },
  NAHARLAGUN: { state: "Arunachal Pradesh", code: "12" },
  PASIGHAT: { state: "Arunachal Pradesh", code: "12" },
  TAWANG: { state: "Arunachal Pradesh", code: "12" },
  ZIRO: { state: "Arunachal Pradesh", code: "12" },

  // ----------------------------------------------------
  // 13: NAGALAND
  // ----------------------------------------------------
  KOHIMA: { state: "Nagaland", code: "13" },
  DIMAPUR: { state: "Nagaland", code: "13" },
  MOKOKCHUNG: { state: "Nagaland", code: "13" },
  TUENSANG: { state: "Nagaland", code: "13" },

  // ----------------------------------------------------
  // 14: MANIPUR
  // ----------------------------------------------------
  IMPHAL: { state: "Manipur", code: "14" },
  CHURACHANDPUR: { state: "Manipur", code: "14" },
  THOUBAL: { state: "Manipur", code: "14" },
  BISHNUPUR: { state: "Manipur", code: "14" },

  // ----------------------------------------------------
  // 15: MIZORAM
  // ----------------------------------------------------
  AIZAWL: { state: "Mizoram", code: "15" },
  LUNGLEI: { state: "Mizoram", code: "15" },
  CHAMPHAI: { state: "Mizoram", code: "15" },
  SERCHHIP: { state: "Mizoram", code: "15" },

  // ----------------------------------------------------
  // 16: TRIPURA
  // ----------------------------------------------------
  AGARTALA: { state: "Tripura", code: "16" },
  DHARMANAGAR: { state: "Tripura", code: "16" },
  UDAIPUR_TRIPURA: { state: "Tripura", code: "16" },
  KAILASHAHAR: { state: "Tripura", code: "16" },

  // ----------------------------------------------------
  // 17: MEGHALAYA
  // ----------------------------------------------------
  SHILLONG: { state: "Meghalaya", code: "17" },
  TURA: { state: "Meghalaya", code: "17" },
  JOWAI: { state: "Meghalaya", code: "17" },
  NONGPOH: { state: "Meghalaya", code: "17" },

  // ----------------------------------------------------
  // 18: ASSAM
  // ----------------------------------------------------
  GUWAHATI: { state: "Assam", code: "18" },
  SILCHAR: { state: "Assam", code: "18" },
  DIBRUGARH: { state: "Assam", code: "18" },
  JORHAT: { state: "Assam", code: "18" },
  NAGAON: { state: "Assam", code: "18" },
  TINSUKIA: { state: "Assam", code: "18" },
  TEZPUR: { state: "Assam", code: "18" },

  // ----------------------------------------------------
  // 19: WEST BENGAL
  // ----------------------------------------------------
  KOLKATA: { state: "West Bengal", code: "19" },
  HOWRAH: { state: "West Bengal", code: "19" },
  ASANSOL: { state: "West Bengal", code: "19" },
  SILIGURI: { state: "West Bengal", code: "19" },
  DURGAPUR: { state: "West Bengal", code: "19" },
  BARDHAMAN: { state: "West Bengal", code: "19" },
  MALDA: { state: "West Bengal", code: "19" },
  KHARAGPUR: { state: "West Bengal", code: "19" },
  DARJEELING: { state: "West Bengal", code: "19" },
  "DAKSHIN DINAJPUR": { state: "West Bengal", code: "19" },
  BALURGHAT: { state: "West Bengal", code: "19" },

  // ----------------------------------------------------
  // 20: JHARKHAND
  // ----------------------------------------------------
  RANCHI: { state: "Jharkhand", code: "20" },
  JAMSHEDPUR: { state: "Jharkhand", code: "20" },
  DHANBAD: { state: "Jharkhand", code: "20" },
  BOKARO: { state: "Jharkhand", code: "20" },
  DEOGHAR: { state: "Jharkhand", code: "20" },
  HAZARIBAGH: { state: "Jharkhand", code: "20" },

  // ----------------------------------------------------
  // 21: ODISHA
  // ----------------------------------------------------
  BHUBANESWAR: { state: "Odisha", code: "21" },
  CUTTACK: { state: "Odisha", code: "21" },
  ROURKELA: { state: "Odisha", code: "21" },
  BERHAMPUR: { state: "Odisha", code: "21" },
  SAMBALPUR: { state: "Odisha", code: "21" },
  PURI: { state: "Odisha", code: "21" },
  BALASORE: { state: "Odisha", code: "21" },
  KHORDA: { state: "Odisha", code: "21" },

  // ----------------------------------------------------
  // 22: CHHATTISGARH
  // ----------------------------------------------------
  RAIPUR: { state: "Chhattisgarh", code: "22" },
  BHILAI: { state: "Chhattisgarh", code: "22" },
  BILASPUR: { state: "Chhattisgarh", code: "22" },
  KORBA: { state: "Chhattisgarh", code: "22" },
  DURG: { state: "Chhattisgarh", code: "22" },
  RAJNANDGAON: { state: "Chhattisgarh", code: "22" },
  JAGDALPUR: { state: "Chhattisgarh", code: "22" },

  // ----------------------------------------------------
  // 23: MADHYA PRADESH
  // ----------------------------------------------------
  INDORE: { state: "Madhya Pradesh", code: "23" },
  BHOPAL: { state: "Madhya Pradesh", code: "23" },
  JABALPUR: { state: "Madhya Pradesh", code: "23" },
  GWALIOR: { state: "Madhya Pradesh", code: "23" },
  UJJAIN: { state: "Madhya Pradesh", code: "23" },
  SAGAR: { state: "Madhya Pradesh", code: "23" },
  REWA: { state: "Madhya Pradesh", code: "23" },
  SATNA: { state: "Madhya Pradesh", code: "23" },
  RATLAM: { state: "Madhya Pradesh", code: "23" },
  DEWAS: { state: "Madhya Pradesh", code: "23" },

  // ----------------------------------------------------
  // 24: GUJARAT
  // ----------------------------------------------------
  AHMEDABAD: { state: "Gujarat", code: "24" },
  SURAT: { state: "Gujarat", code: "24" },
  VADODARA: { state: "Gujarat", code: "24" },
  RAJKOT: { state: "Gujarat", code: "24" },
  BHAVNAGAR: { state: "Gujarat", code: "24" },
  JAMNAGAR: { state: "Gujarat", code: "24" },
  GANDHINAGAR: { state: "Gujarat", code: "24" },
  JUNAGADH: { state: "Gujarat", code: "24" },
  ANAND: { state: "Gujarat", code: "24" },
  BHUJ: { state: "Gujarat", code: "24" },
  VAPI: { state: "Gujarat", code: "24" },

  // ----------------------------------------------------
  // 26: DADRA & NAGAR HAVELI AND DAMAN & DIU (UT)
  // ----------------------------------------------------
  DAMAN: { state: "Dadra and Nagar Haveli and Daman and Diu", code: "26" },
  DIU: { state: "Dadra and Nagar Haveli and Daman and Diu", code: "26" },
  SILVASSA: { state: "Dadra and Nagar Haveli and Daman and Diu", code: "26" },

  // ----------------------------------------------------
  // 27: MAHARASHTRA
  // ----------------------------------------------------
  MUMBAI: { state: "Maharashtra", code: "27" },
  PUNE: { state: "Maharashtra", code: "27" },
  NAGPUR: { state: "Maharashtra", code: "27" },
  THANE: { state: "Maharashtra", code: "27" },
  "NAVI MUMBAI": { state: "Maharashtra", code: "27" },
  NASHIK: { state: "Maharashtra", code: "27" },
  AURANGABAD: { state: "Maharashtra", code: "27" },
  "CHHATRAPATI SAMBHAJINAGAR": { state: "Maharashtra", code: "27" },
  SOLAPUR: { state: "Maharashtra", code: "27" },
  AMRAVATI: { state: "Maharashtra", code: "27" },
  KOLHAPUR: { state: "Maharashtra", code: "27" },
  SANGLI: { state: "Maharashtra", code: "27" },
  JALGAON: { state: "Maharashtra", code: "27" },
  LATUR: { state: "Maharashtra", code: "27" },
  NANDED: { state: "Maharashtra", code: "27" },

  // ----------------------------------------------------
  // 29: KARNATAKA
  // ----------------------------------------------------
  BANGALORE: { state: "Karnataka", code: "29" },
  BENGALURU: { state: "Karnataka", code: "29" },
  MYSURU: { state: "Karnataka", code: "29" },
  MYSORE: { state: "Karnataka", code: "29" },
  HUBLI: { state: "Karnataka", code: "29" },
  HUBBALLI: { state: "Karnataka", code: "29" },
  DHARWAD: { state: "Karnataka", code: "29" },
  MANGALORE: { state: "Karnataka", code: "29" },
  MANGALURU: { state: "Karnataka", code: "29" },
  BELGAUM: { state: "Karnataka", code: "29" },
  BELAGAVI: { state: "Karnataka", code: "29" },
  GULBARGA: { state: "Karnataka", code: "29" },
  KALABURAGI: { state: "Karnataka", code: "29" },
  DAVANAGERE: { state: "Karnataka", code: "29" },
  BELLARY: { state: "Karnataka", code: "29" },
  BALLARI: { state: "Karnataka", code: "29" },
  BIDAR: { state: "Karnataka", code: "29" },
  CHITRADURGA: { state: "Karnataka", code: "29" },
  SHIVAMOGGA: { state: "Karnataka", code: "29" },

  // ----------------------------------------------------
  // 30: GOA
  // ----------------------------------------------------
  GOA: { state: "Goa", code: "30" },
  PANAJI: { state: "Goa", code: "30" },
  MARGAO: { state: "Goa", code: "30" },
  VASCO: { state: "Goa", code: "30" },
  "VASCO DA GAMA": { state: "Goa", code: "30" },
  MAPUSA: { state: "Goa", code: "30" },
  PONDA: { state: "Goa", code: "30" },

  // ----------------------------------------------------
  // 31: LAKSHADWEEP (UT)
  // ----------------------------------------------------
  KAVARATTI: { state: "Lakshadweep", code: "31" },
  AGATTI: { state: "Lakshadweep", code: "31" },
  AMINI: { state: "Lakshadweep", code: "31" },
  ANDROTT: { state: "Lakshadweep", code: "31" },

  // ----------------------------------------------------
  // 32: KERALA
  // ----------------------------------------------------
  THIRUVANANTHAPURAM: { state: "Kerala", code: "32" },
  TRIVANDRUM: { state: "Kerala", code: "32" },
  KOCHI: { state: "Kerala", code: "32" },
  COCHIN: { state: "Kerala", code: "32" },
  KOZHIKODE: { state: "Kerala", code: "32" },
  CALICUT: { state: "Kerala", code: "32" },
  THRISSUR: { state: "Kerala", code: "32" },
  KOLLAM: { state: "Kerala", code: "32" },
  PALAKKAD: { state: "Kerala", code: "32" },
  ALAPPUZHA: { state: "Kerala", code: "32" },
  ALLEPPEY: { state: "Kerala", code: "32" },
  KANNUR: { state: "Kerala", code: "32" },
  KOTTAYAM: { state: "Kerala", code: "32" },

  // ----------------------------------------------------
  // 33: TAMIL NADU
  // ----------------------------------------------------
  CHENNAI: { state: "Tamil Nadu", code: "33" },
  COIMBATORE: { state: "Tamil Nadu", code: "33" },
  MADURAI: { state: "Tamil Nadu", code: "33" },
  TIRUCHIRAPPALLI: { state: "Tamil Nadu", code: "33" },
  TRICHY: { state: "Tamil Nadu", code: "33" },
  SALEM: { state: "Tamil Nadu", code: "33" },
  TIRUNELVELI: { state: "Tamil Nadu", code: "33" },
  TIRUPPUR: { state: "Tamil Nadu", code: "33" },
  ERODE: { state: "Tamil Nadu", code: "33" },
  VELLORE: { state: "Tamil Nadu", code: "33" },
  THOOTHUKUDI: { state: "Tamil Nadu", code: "33" },
  TUTICORIN: { state: "Tamil Nadu", code: "33" },

  // ----------------------------------------------------
  // 34: PUDUCHERRY (UT)
  // ----------------------------------------------------
  PUDUCHERRY: { state: "Puducherry", code: "34" },
  PONDICHERRY: { state: "Puducherry", code: "34" },
  KARAIKAL: { state: "Puducherry", code: "34" },
  MAHE: { state: "Puducherry", code: "34" },
  YANAM: { state: "Puducherry", code: "34" },

  // ----------------------------------------------------
  // 35: ANDAMAN AND NICOBAR ISLANDS (UT)
  // ----------------------------------------------------
  "PORT BLAIR": { state: "Andaman and Nicobar Islands", code: "35" },
  HAVELOCK: { state: "Andaman and Nicobar Islands", code: "35" },
  "SRI VIJAYA PURAM": { state: "Andaman and Nicobar Islands", code: "35" },

  // ----------------------------------------------------
  // 36: TELANGANA
  // ----------------------------------------------------
  HYDERABAD: { state: "Telangana", code: "36" },
  SECUNDERABAD: { state: "Telangana", code: "36" },
  WARANGAL: { state: "Telangana", code: "36" },
  HANAMKONDA: { state: "Telangana", code: "36" },
  NIZAMABAD: { state: "Telangana", code: "36" },
  KARIMNAGAR: { state: "Telangana", code: "36" },
  RAMAGUNDAM: { state: "Telangana", code: "36" },
  KHAMMAM: { state: "Telangana", code: "36" },
  "K.V. RANGAREDDY": { state: "Telangana", code: "36" },
  RANGAREDDY: { state: "Telangana", code: "36" },
  NALGONDA: { state: "Telangana", code: "36" },
  MEDAK: { state: "Telangana", code: "36" },
  MAHBUBNAGAR: { state: "Telangana", code: "36" },

  // ----------------------------------------------------
  // 37: ANDHRA PRADESH
  // ----------------------------------------------------
  VISAKHAPATNAM: { state: "Andhra Pradesh", code: "37" },
  VIJAYAWADA: { state: "Andhra Pradesh", code: "37" },
  GUNTUR: { state: "Andhra Pradesh", code: "37" },
  NELLORE: { state: "Andhra Pradesh", code: "37" },
  KURNOOL: { state: "Andhra Pradesh", code: "37" },
  RAJAHMUNDRY: { state: "Andhra Pradesh", code: "37" },
  TIRUPATI: { state: "Andhra Pradesh", code: "37" },
  KADAPA: { state: "Andhra Pradesh", code: "37" },
  ANANTAPUR: { state: "Andhra Pradesh", code: "37" },
  ELURU: { state: "Andhra Pradesh", code: "37" },

  // ----------------------------------------------------
  // 38: LADAKH (UT)
  // ----------------------------------------------------
  LEH: { state: "Ladakh", code: "38" },
  KARGIL: { state: "Ladakh", code: "38" },
};

const ALL_INDIAN_STATES = {
  "01": { state: "Jammu and Kashmir", code: "01" },
  "02": { state: "Himachal Pradesh", code: "02" },
  "03": { state: "Punjab", code: "03" },
  "04": { state: "Chandigarh", code: "04" },
  "05": { state: "Uttarakhand", code: "05" },
  "06": { state: "Haryana", code: "06" },
  "07": { state: "Delhi", code: "07" },
  "08": { state: "Rajasthan", code: "08" },
  "09": { state: "Uttar Pradesh", code: "09" },
  "10": { state: "Bihar", code: "10" },
  "11": { state: "Sikkim", code: "11" },
  "12": { state: "Arunachal Pradesh", code: "12" },
  "13": { state: "Nagaland", code: "13" },
  "14": { state: "Manipur", code: "14" },
  "15": { state: "Mizoram", code: "15" },
  "16": { state: "Tripura", code: "16" },
  "17": { state: "Meghalaya", code: "17" },
  "18": { state: "Assam", code: "18" },
  "19": { state: "West Bengal", code: "19" },
  "20": { state: "Jharkhand", code: "20" },
  "21": { state: "Odisha", code: "21" },
  "22": { state: "Chhattisgarh", code: "22" },
  "23": { state: "Madhya Pradesh", code: "23" },
  "24": { state: "Gujarat", code: "24" },
  "26": { state: "Dadra and Nagar Haveli and Daman and Diu", code: "26" },
  "27": { state: "Maharashtra", code: "27" },
  "29": { state: "Karnataka", code: "29" },
  "30": { state: "Goa", code: "30" },
  "31": { state: "Lakshadweep", code: "31" },
  "32": { state: "Kerala", code: "32" },
  "33": { state: "Tamil Nadu", code: "33" },
  "34": { state: "Puducherry", code: "34" },
  "35": { state: "Andaman and Nicobar Islands", code: "35" },
  "36": { state: "Telangana", code: "36" },
  "37": { state: "Andhra Pradesh", code: "37" },
  "38": { state: "Ladakh", code: "38" },
  BIHAR: { state: "Bihar", code: "10" },
  ODISHA: { state: "Odisha", code: "21" },
  ORISSA: { state: "Odisha", code: "21" },
  MAHARASHTRA: { state: "Maharashtra", code: "27" },
  DELHI: { state: "Delhi", code: "07" },
  "UTTAR PRADESH": { state: "Uttar Pradesh", code: "09" },
  UP: { state: "Uttar Pradesh", code: "09" },
  "MADHYA PRADESH": { state: "Madhya Pradesh", code: "23" },
  MP: { state: "Madhya Pradesh", code: "23" },
  GUJARAT: { state: "Gujarat", code: "24" },
  RAJASTHAN: { state: "Rajasthan", code: "08" },
  "WEST BENGAL": { state: "West Bengal", code: "19" },
  JHARKHAND: { state: "Jharkhand", code: "20" },
  CHHATTISGARH: { state: "Chhattisgarh", code: "22" },
  KARNATAKA: { state: "Karnataka", code: "29" },
  TELANGANA: { state: "Telangana", code: "36" },
  "ANDHRA PRADESH": { state: "Andhra Pradesh", code: "37" },
  "TAMIL NADU": { state: "Tamil Nadu", code: "33" },
  KERALA: { state: "Kerala", code: "32" },
  PUNJAB: { state: "Punjab", code: "03" },
  HARYANA: { state: "Haryana", code: "06" },
  UTTARAKHAND: { state: "Uttarakhand", code: "05" },
  ASSAM: { state: "Assam", code: "18" }
};

export const INDIAN_STATES_LIST = [
  { code: "10", name: "Bihar" },
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
];

export const PAYMENT_MODES_LIST = [
  "UPI",
  "Bank Transfer",
  "Net Banking",
  "Credit/Debit Card",
  "Cheque",
  "Cash",
];

const resolveStateFromCity = (cityName, stateName = "") => {
  if (stateName) {
    const cleanState = String(stateName).trim().toUpperCase();
    if (ALL_INDIAN_STATES[cleanState]) return ALL_INDIAN_STATES[cleanState];
    for (const [k, v] of Object.entries(ALL_INDIAN_STATES)) {
      if (cleanState.includes(k) || k.includes(cleanState)) return v;
    }
  }
  if (!cityName) return { state: "Bihar", code: "10" };
  const cleaned = String(cityName).trim().toUpperCase();
  if (INDIAN_CITY_STATE_MAP[cleaned]) return INDIAN_CITY_STATE_MAP[cleaned];
  for (const [k, v] of Object.entries(INDIAN_CITY_STATE_MAP)) {
    if (cleaned.includes(k) || k.includes(cleaned)) return v;
  }
  return { state: "Bihar", code: "10" };
};

export const ENTITY_NAME = "equity";

export const INVOICE_STATUS_CONFIG = [
  {
    id: "Created",
    label: "Created",
    description: "Initial statutory draft created in the system",
    badgeBg: "bg-blue-50",
    badgeText: "text-blue-700",
    badgeBorder: "border-blue-200",
    icon: "clock",
  },
  {
    id: "Send to Client",
    label: "Send to Client",
    description: "Dispatched / sent to client for review and settlement",
    badgeBg: "bg-indigo-50",
    badgeText: "text-indigo-700",
    badgeBorder: "border-indigo-200",
    icon: "paper-plane",
  },
  {
    id: "Completed",
    label: "Completed",
    description: "Payment confirmed, invoice settled, and service active",
    badgeBg: "bg-emerald-50",
    badgeText: "text-emerald-700",
    badgeBorder: "border-emerald-200",
    icon: "check-circle",
  },
  {
    id: "Cancelled",
    label: "Cancelled",
    description: "Invoice voided or cancelled",
    badgeBg: "bg-rose-50",
    badgeText: "text-rose-700",
    badgeBorder: "border-rose-200",
    icon: "times-circle",
  },
];

const sanitizeFilename = (str) => {
  if (!str) return "NA";
  return String(str)
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, "_");
};

const Logo = () => (
  <img
    src="/images/pdf_logo.png"
    alt="Logo"
    className="w-32 h-24 object-contain object-left shrink-0"
  />
);

const EquityReaserchMartLogo = Logo;

const formatTallyDate = (d) => {
  if (!d) return "";
  const dateObj = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dateObj.getTime())) return String(d);
  const day = dateObj.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[dateObj.getMonth()];
  const year = String(dateObj.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

const formatCurrency = (val) => {
  if (val === undefined || val === null || isNaN(Number(val))) return "0.00";
  return Number(val).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSlashDate = (d) => {
  if (!d) return "";
  const dateObj = typeof d === "string" ? new Date(d) : d;
  if (isNaN(dateObj.getTime())) return String(d);
  const dd = String(dateObj.getDate()).padStart(2, "0");
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const yyyy = dateObj.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const numberToWordsIndian = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "";
  const absNum = Math.abs(num);
  const rupees = Math.floor(absNum);
  const paise = Math.round((absNum - rupees) * 100);

  if (rupees === 0 && paise === 0) return "Zero Rupees only";

  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanOneThousand = (number) => {
    let current;
    if (number % 100 < 20) {
      current = a[number % 100];
      number = Math.floor(number / 100);
    } else {
      current = a[number % 10];
      number = Math.floor(number / 10);
      current = b[number % 10] + (current ? " " + current : "");
      number = Math.floor(number / 10);
    }
    if (number === 0) return current;
    return a[number] + " Hundred" + (current ? " " + current : "");
  };

  const convertNumber = (n) => {
    let res = "";
    const crore = Math.floor(n / 10000000);
    const lakh = Math.floor((n % 10000000) / 100000);
    const thousand = Math.floor((n % 100000) / 1000);
    const remainder = n % 1000;

    if (crore > 0) res += convertLessThanOneThousand(crore) + " Crore ";
    if (lakh > 0) res += convertLessThanOneThousand(lakh) + " Lakh ";
    if (thousand > 0) res += convertLessThanOneThousand(thousand) + " Thousand ";
    if (remainder > 0) res += convertLessThanOneThousand(remainder);
    return res.trim();
  };

  let result = "";
  if (rupees > 0) {
    result = `${convertNumber(rupees)} Rupees`;
  }
  if (paise > 0) {
    result += (result ? " and " : "") + `${convertNumber(paise)} Paise`;
  }
  return `${result.trim()} only`;
};

const TallyTaxInvoiceTemplate = ({
  invoice,
  entityProfile,
  signatureUrl = null,
  onSignatureClick = null,
}) => {
  if (!invoice) return null;
  const raw = invoice.raw || {};
  const isIntraState = raw.isIntraState ?? (raw.buyerStateCode === "23");

  const sellerName =
    raw.sellerName ||
    entityProfile?.companyName ||
    entityProfile?.CompanyName ||
    "Equity Research Mart";
  const sellerAddress =
    raw.sellerAddress ||
    entityProfile?.officeAddress ||
    entityProfile?.OfficeAddress ||
    "503, A, Patliputra Mall, Block - B, Exhibition Road, Patna";
  const sellerGstin =
    raw.sellerGstin ||
    entityProfile?.gstin ||
    entityProfile?.Gstin ||
    "10BJKPR9978L1ZB";
  const sellerStateName =
    raw.sellerStateName ||
    entityProfile?.stateName ||
    entityProfile?.StateName ||
    "Bihar";
  const sellerStateCode =
    raw.sellerStateCode ||
    entityProfile?.stateCode ||
    entityProfile?.StateCode ||
    "10";
  const sellerCin =
    raw.sellerCin ||
    entityProfile?.cin ||
    entityProfile?.Cin ||
    "NA";
  const sellerContact =
    raw.sellerContact ||
    entityProfile?.contactPhone ||
    entityProfile?.contactNumber ||
    entityProfile?.ContactPhone ||
    "+91 70044 93381";
  const sellerEmail =
    raw.sellerEmail ||
    entityProfile?.email ||
    entityProfile?.officialEmail ||
    entityProfile?.Email ||
    "compliance@equityresearchmart.in";
  const sellerPan =
    raw.sellerPan ||
    entityProfile?.pan ||
    entityProfile?.panNumber ||
    entityProfile?.Pan ||
    "N/A";
  const activeSignature =
    raw.signatureUrl ||
    signatureUrl ||
    entityProfile?.signatureImageUrl ||
    entityProfile?.signatureUrl;

  const consigneeName = raw.consigneeName || invoice.customerName || "KRISHNA KUMAR YADAV";
  const consigneeAddress =
    raw.consigneeAddress ||
    raw.buyerAddress ||
    "NA";
  const consigneeGstin = raw.consigneeGstin || raw.buyerGstin || "NA";
  const consigneeStateName = raw.consigneeStateName || raw.buyerStateName || "NA";
  const consigneeStateCode = raw.consigneeStateCode || raw.buyerStateCode || "NA";

  const buyerName = raw.buyerName || invoice.customerName || "NA";
  const buyerAddress =
    raw.buyerAddress ||
    "NA";
  const buyerGstin = raw.buyerGstin || "NA";
  const buyerStateName = raw.buyerStateName || "NA";
  const buyerStateCode = raw.buyerStateCode || "NA";

  const invoiceNo = invoice.id || "NA";
  const datedStr = formatTallyDate(invoice.date || raw.invoiceDate) || "NA";

  const bankAccountHolder =
    raw.bankAccountHolderName || entityProfile?.bankAccountHolderName || "NA";
  const bankName = raw.bankName || entityProfile?.bankName || "NA";
  const bankAccountNo = raw.bankAccountNo || entityProfile?.bankAccountNo || "NA";
  const bankBranchAndIfsc =
    raw.bankBranchAndIfsc ||
    entityProfile?.bankBranchAndIfsc ||
    "NA";
  const bankSwiftCode = raw.bankSwiftCode || "";
  const upiId =
    raw.upiId ||
    entityProfile?.upiId ||
    entityProfile?.UpiId ||
    entityProfile?.upi_id ||
    "yespay.bizsbiz118420@yesbankltd";
  const cleanUpiId = (upiId || "").trim();

  const serviceDescription = invoice.service || raw.serviceDescription || "Services";
  const hsnSac = raw.primaryHsnSac || (invoice.items && invoice.items[0]?.hsn_sac) || "998222";

  const taxableVal = Number(invoice.amount || 0);
  const totalTaxVal = Number(invoice.gst || 0);
  const grandTotalVal = Number(invoice.total || 0);

  const cgstRate = Number(raw.cgstRate ?? 9);
  const cgstAmt = isIntraState ? Number(raw.cgstAmount ?? totalTaxVal / 2) : 0;

  const sgstRate = Number(raw.sgstRate ?? 9);
  const sgstAmt = isIntraState ? Number(raw.sgstAmount ?? totalTaxVal / 2) : 0;

  const igstRate = Number(raw.igstRate ?? 18);
  const igstAmt = isIntraState ? 0 : Number(raw.igstAmount ?? totalTaxVal);

  const amountInWords =
    numberToWordsIndian(grandTotalVal) || raw.amountInWords || `INR ${grandTotalVal.toLocaleString("en-IN")} Only`;
  const taxAmountInWords =
    numberToWordsIndian(totalTaxVal) || raw.taxAmountInWords || `INR ${totalTaxVal.toLocaleString("en-IN")} Only`;

  const isGstBilling =
    invoice.isGstInvoice !== false &&
    raw.isGstInvoice !== false &&
    (Number(invoice.gst || raw.totalTaxAmount || 0) > 0 || invoice.isGstInvoice === true || raw.isGstInvoice === true);

  const renderAddressLines = (addr) => {
    if (!addr) return null;
    const lines = String(addr).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    return lines.map((line, i) => (
      <div key={i} className="uppercase leading-tight font-medium text-black break-words">
        {line}
      </div>
    ));
  };

  return (
    <div className="w-full max-w-[820px] mx-auto text-black font-sans text-[11px] leading-tight select-none print:max-w-none print:w-full print:text-[10px]">
      {/* Centered Top Heading */}
      <div className="text-center font-bold text-sm tracking-wide py-1 text-black">
        {isGstBilling ? "TAX INVOICE" : "INVOICE"}
      </div>

      {/* Main Outer Box with clean 1px solid black border */}
      <div className="border border-black bg-white">
        {/* Top Split Section: Left (Seller, Consignee, Buyer) | Right (Dispatch & Invoicing Grid) */}
        <div className="grid grid-cols-2 border-b border-black">
          {/* Left Column */}
          <div className="border-r border-black">
            {/* Seller Header */}
            <div className="p-2 border-b border-black flex items-start gap-3">
              <EquityReaserchMartLogo />
              <div className="text-[10px] leading-tight space-y-0.5 text-black flex-1 min-w-0">
                <div className="font-bold text-xs text-black">{sellerName}</div>
                <div className="text-black leading-tight break-words">
                  {String(sellerAddress)
                    .split(/\r?\n/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                </div>
                <div>
                  <strong>GSTIN/UIN:</strong> {sellerGstin}
                </div>
                <div>
                  <strong>State Name :</strong> {sellerStateName}, <strong>Code :</strong> {sellerStateCode}
                </div>
                <div>
                  <strong>CIN:</strong> {sellerCin}
                </div>
                <div>
                  <strong>Contact :</strong> {sellerContact}
                </div>
                <div>
                  <strong>E-Mail :</strong> {sellerEmail}
                </div>
              </div>
            </div>

            {/* Consignee (Ship to) */}
            <div className="p-2 border-b border-black text-[10px] leading-tight space-y-0.5">
              <div className="text-gray-800">Consignee (Ship to)</div>
              <div className="font-bold text-xs uppercase text-black">{consigneeName}</div>
              {renderAddressLines(consigneeAddress)}
              <div className="pt-0.5 grid grid-cols-[85px_1fr]">
                <span>GSTIN/UIN</span>
                <span>: {consigneeGstin || "N/A"}</span>
              </div>
              <div className="grid grid-cols-[85px_1fr]">
                <span>State Name</span>
                <span>: {consigneeStateName}, Code : {consigneeStateCode}</span>
              </div>
            </div>

            {/* Buyer (Bill to) */}
            <div className="p-2 text-[10px] leading-tight space-y-0.5">
              <div className="text-gray-800">Buyer (Bill to)</div>
              <div className="font-bold text-xs uppercase text-black">{buyerName}</div>
              {renderAddressLines(buyerAddress)}
              <div className="pt-0.5 grid grid-cols-[85px_1fr]">
                <span>GSTIN/UIN</span>
                <span>: {buyerGstin || "N/A"}</span>
              </div>
              <div className="grid grid-cols-[85px_1fr]">
                <span>State Name</span>
                <span>: {buyerStateName}, Code : {buyerStateCode}</span>
              </div>
            </div>
          </div>

          {/* Right Column: Invoicing & Dispatch Metadata */}
          <div className="divide-y divide-black text-[10px] leading-snug">
            {/* Row 1: Invoice No & Dated */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[36px]">
                <div className="text-gray-700">Invoice No.</div>
                <div className="font-bold text-xs text-black">{invoiceNo}</div>
              </div>
              <div className="p-1.5 min-h-[36px]">
                <div className="text-gray-700">Dated</div>
                <div className="font-bold text-xs text-black">{datedStr}</div>
              </div>
            </div>

            {/* Row 2: Delivery Note & Mode/Terms of Payment */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Delivery Note</div>
                <div>{raw.deliveryNote || ""}</div>
              </div>
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Mode/Terms of Payment</div>
                <div>{raw.modeOrTermsOfPayment || ""}</div>
              </div>
            </div>

            {/* Row 3: Reference No. & Date & Other References */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Reference No. & Date.</div>
                <div>{raw.referenceNoAndDate || ""}</div>
              </div>
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Other References</div>
                <div>{raw.otherReferences || ""}</div>
              </div>
            </div>

            {/* Row 4: Buyer's Order No. & Dated */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Buyer’s Order No.</div>
                <div>{raw.buyersOrderNo || ""}</div>
              </div>
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Dated</div>
                <div>{formatTallyDate(raw.orderDate) || ""}</div>
              </div>
            </div>

            {/* Row 5: Dispatch Doc No. & Delivery Note Date */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Dispatch Doc No.</div>
                <div>{raw.dispatchDocNo || ""}</div>
              </div>
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Delivery Note Date</div>
                <div>{formatTallyDate(raw.deliveryNoteDate) || ""}</div>
              </div>
            </div>

            {/* Row 6: Dispatched through & Destination */}
            <div className="grid grid-cols-2 divide-x divide-black">
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Dispatched through</div>
                <div>{raw.dispatchedThrough || ""}</div>
              </div>
              <div className="p-1.5 min-h-[32px]">
                <div className="text-gray-700">Destination</div>
                <div>{raw.destination || ""}</div>
              </div>
            </div>

            {/* Row 7: Terms of Delivery */}
            <div className="p-1.5 min-h-[38px]">
              <div className="text-gray-700">Terms of Delivery</div>
              <div>{raw.termsOfDelivery || ""}</div>
            </div>
          </div>
        </div>

        {/* Services & Charges Table */}
        <div className="border-b border-black">
          <table className="w-full border-separate border-spacing-0 text-[10px]">
            <thead>
              <tr className="text-center leading-tight">
                <th className="p-1 border-r border-b border-black w-8 font-normal">
                  <div>Sl</div>
                  <div>No.</div>
                </th>
                <th className="p-1 border-r border-b border-black font-normal">
                  <div>Description of</div>
                  <div>Services</div>
                </th>
                <th className="p-1 border-r border-b border-black w-20 font-normal">HSN/SAC</th>
                <th className="p-1 border-r border-b border-black w-16 font-normal">Quantity</th>
                <th className="p-1 border-r border-b border-black w-18 font-normal">Rate</th>
                <th className="p-1 border-r border-b border-black w-12 font-normal">per</th>
                <th className="p-1 border-b border-black w-28 font-normal text-center">Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* Service Line + Tax lines inside Description column */}
              <tr className="align-top">
                <td className="p-1.5 border-r border-black text-center font-normal">1</td>
                <td className="p-1.5 border-r border-black text-right font-bold italic space-y-1">
                  <div>{serviceDescription}</div>
                  {isIntraState ? (
                    <>
                      <div>CGST@9%</div>
                      <div>SGST@9%</div>
                    </>
                  ) : (
                    <div>IGST@18%</div>
                  )}
                </td>
                <td className="p-1.5 border-r border-black text-center font-mono">
                  {hsnSac}
                </td>
                <td className="p-1.5 border-r border-black text-center"></td>
                <td className="p-1.5 border-r border-black text-right font-mono"></td>
                <td className="p-1.5 border-r border-black text-center"></td>
                <td className="p-1.5 text-right font-mono font-bold space-y-1">
                  <div>{formatCurrency(taxableVal)}</div>
                  {isIntraState ? (
                    <>
                      <div>{formatCurrency(cgstAmt)}</div>
                      <div>{formatCurrency(sgstAmt)}</div>
                    </>
                  ) : (
                    <div>{formatCurrency(igstAmt)}</div>
                  )}
                </td>
              </tr>

              {/* Vertical spacing filler */}
              <tr className="h-44">
                <td className="border-r border-black"></td>
                <td className="border-r border-black"></td>
                <td className="border-r border-black"></td>
                <td className="border-r border-black"></td>
                <td className="border-r border-black"></td>
                <td className="border-r border-black"></td>
                <td></td>
              </tr>

              {/* Total Row */}
              <tr className="font-bold text-[11px]">
                <td className="pt-1.5 pb-2.5 px-1 border-r border-t border-black"></td>
                <td className="pt-1.5 pb-2.5 px-2 border-r border-t border-black text-center font-bold">Total</td>
                <td className="pt-1.5 pb-2.5 px-1 border-r border-t border-black"></td>
                <td className="pt-1.5 pb-2.5 px-1 border-r border-t border-black"></td>
                <td className="pt-1.5 pb-2.5 px-1 border-r border-t border-black"></td>
                <td className="pt-1.5 pb-2.5 px-1 border-r border-t border-black"></td>
                <td className="pt-1.5 pb-2.5 px-2 border-t border-black text-right font-mono font-bold">
                  ₹ {formatCurrency(grandTotalVal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount Chargeable (in words) & E. & O.E */}
        <div className="pt-1.5 pb-2 px-2 border-b border-black text-[10px]">
          <div className="flex justify-between items-center text-gray-700">
            <span>Amount Chargeable (in words)</span>
            <span className="italic font-serif">E. & O.E</span>
          </div>
          <div className="font-bold text-xs text-black mt-0.5">
            {amountInWords}
          </div>
        </div>

        {/* Tax Breakdown Table */}
        <div className="border-b border-black text-[10px]">
          {isIntraState ? (
            <div>
              {/* Row 1: Header */}
              <div className="flex border-b border-black items-stretch text-center">
                {/* 1. HSN/SAC */}
                <div className="w-[22%] border-r border-black flex items-center justify-center font-medium p-1.5">
                  HSN/SAC
                </div>

                {/* 2. Taxable Value */}
                <div className="w-[18%] border-r border-black flex flex-col items-center justify-center font-medium p-1.5 leading-tight">
                  <span>Taxable</span>
                  <span>Value</span>
                </div>

                {/* 3. CGST */}
                <div className="w-[22%] border-r border-black flex flex-col">
                  <div className="py-1 text-center font-medium border-b border-black">CGST</div>
                  <div className="flex flex-1 text-center items-center">
                    <div className="w-[42%] py-1.5 border-r border-black">Rate</div>
                    <div className="w-[58%] py-1.5">Amount</div>
                  </div>
                </div>

                {/* 4. SGST / UTGST */}
                <div className="w-[22%] border-r border-black flex flex-col">
                  <div className="py-1 text-center font-medium border-b border-black">SGST/UTGST</div>
                  <div className="flex flex-1 text-center items-center">
                    <div className="w-[42%] py-1.5 border-r border-black">Rate</div>
                    <div className="w-[58%] py-1.5">Amount</div>
                  </div>
                </div>

                {/* 5. Total Tax Amount */}
                <div className="w-[16%] flex flex-col items-center justify-center font-medium p-1.5 leading-tight">
                  <span>Total</span>
                  <span>Tax Amount</span>
                </div>
              </div>

              {/* Row 2: Data */}
              <div className="flex border-b border-black items-center text-center">
                <div className="w-[22%] border-r border-black pt-1.5 pb-2 px-2 text-left font-mono">
                  {hsnSac}
                </div>
                <div className="w-[18%] border-r border-black pt-1.5 pb-2 px-2 text-right font-mono">
                  {formatCurrency(taxableVal)}
                </div>
                <div className="w-[22%] border-r border-black flex items-center">
                  <div className="w-[42%] pt-1.5 pb-2 px-1 border-r border-black text-center font-mono">{cgstRate}%</div>
                  <div className="w-[58%] pt-1.5 pb-2 px-2 text-right font-mono">{formatCurrency(cgstAmt)}</div>
                </div>
                <div className="w-[22%] border-r border-black flex items-center">
                  <div className="w-[42%] pt-1.5 pb-2 px-1 border-r border-black text-center font-mono">{sgstRate}%</div>
                  <div className="w-[58%] pt-1.5 pb-2 px-2 text-right font-mono">{formatCurrency(sgstAmt)}</div>
                </div>
                <div className="w-[16%] pt-1.5 pb-2 px-2 text-right font-mono font-bold">
                  {formatCurrency(totalTaxVal)}
                </div>
              </div>

              {/* Row 3: Total */}
              <div className="flex items-center text-center font-bold">
                <div className="w-[22%] border-r border-black pt-1.5 pb-2.5 px-2 text-right font-bold">
                  Total
                </div>
                <div className="w-[18%] border-r border-black pt-1.5 pb-2.5 px-2 text-right font-mono">
                  {formatCurrency(taxableVal)}
                </div>
                <div className="w-[22%] border-r border-black flex items-center">
                  <div className="w-[42%] pt-1.5 pb-2.5 border-r border-black"></div>
                  <div className="w-[58%] pt-1.5 pb-2.5 px-2 text-right font-mono font-bold">{formatCurrency(cgstAmt)}</div>
                </div>
                <div className="w-[22%] border-r border-black flex items-center">
                  <div className="w-[42%] pt-1.5 pb-2.5 border-r border-black"></div>
                  <div className="w-[58%] pt-1.5 pb-2.5 px-2 text-right font-mono font-bold">{formatCurrency(sgstAmt)}</div>
                </div>
                <div className="w-[16%] pt-1.5 pb-2.5 px-2 text-right font-mono font-bold">
                  {formatCurrency(totalTaxVal)}
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Row 1: Header */}
              <div className="flex border-b border-black items-stretch text-center">
                {/* 1. HSN/SAC */}
                <div className="w-[30%] border-r border-black flex items-center justify-center font-medium p-1.5">
                  HSN/SAC
                </div>

                {/* 2. Taxable Value */}
                <div className="w-[24%] border-r border-black flex flex-col items-center justify-center font-medium p-1.5 leading-tight">
                  <span>Taxable</span>
                  <span>Value</span>
                </div>

                {/* 3. IGST */}
                <div className="w-[24%] border-r border-black flex flex-col">
                  <div className="py-1 text-center font-medium border-b border-black">IGST</div>
                  <div className="flex flex-1 text-center items-center">
                    <div className="w-[40%] py-1.5 border-r border-black">Rate</div>
                    <div className="w-[60%] py-1.5">Amount</div>
                  </div>
                </div>

                {/* 4. Total Tax Amount */}
                <div className="w-[22%] flex flex-col items-center justify-center font-medium p-1.5 leading-tight">
                  <span>Total</span>
                  <span>Tax Amount</span>
                </div>
              </div>

              {/* Row 2: Data */}
              <div className="flex border-b border-black items-center text-center">
                <div className="w-[30%] border-r border-black pt-1.5 pb-2 px-2 text-left font-mono">
                  {hsnSac}
                </div>
                <div className="w-[24%] border-r border-black pt-1.5 pb-2 px-2 text-right font-mono">
                  {formatCurrency(taxableVal)}
                </div>
                <div className="w-[24%] border-r border-black flex items-center">
                  <div className="w-[40%] pt-1.5 pb-2 px-1 border-r border-black text-center font-mono">{igstRate}%</div>
                  <div className="w-[60%] pt-1.5 pb-2 px-2 text-right font-mono">{formatCurrency(igstAmt)}</div>
                </div>
                <div className="w-[22%] pt-1.5 pb-2 px-2 text-right font-mono font-bold">
                  {formatCurrency(totalTaxVal)}
                </div>
              </div>

              {/* Row 3: Total */}
              <div className="flex items-center text-center font-bold">
                <div className="w-[30%] border-r border-black pt-1.5 pb-2.5 px-2 text-right font-bold">
                  Total
                </div>
                <div className="w-[24%] border-r border-black pt-1.5 pb-2.5 px-2 text-right font-mono">
                  {formatCurrency(taxableVal)}
                </div>
                <div className="w-[24%] border-r border-black flex items-center">
                  <div className="w-[40%] pt-1.5 pb-2.5 border-r border-black"></div>
                  <div className="w-[60%] pt-1.5 pb-2.5 px-2 text-right font-mono font-bold">{formatCurrency(igstAmt)}</div>
                </div>
                <div className="w-[22%] pt-1.5 pb-2.5 px-2 text-right font-mono font-bold">
                  {formatCurrency(totalTaxVal)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section Matching Exact Statutory Layout */}
        <div className="text-[10px] leading-tight text-black">
          {/* Upper Section: Tax Amount (in words), Bank Details, and PAN */}
          <div className="p-2 pb-1.5 grid grid-cols-2">
            {/* Left Side: Tax Amount (in words) at top, Company's PAN at bottom */}
            <div className="flex flex-col justify-between pr-2">
              <div>
                <span>Tax Amount (in words) : </span>
                <strong className="font-bold text-black">{taxAmountInWords}</strong>
              </div>
              <div className="pt-6 sm:pt-8 print:pt-3">
                <span>Company's PAN</span>
                <span className="ml-8">: </span>
                <strong className="font-bold font-mono text-black">{sellerPan}</strong>
              </div>
            </div>

            {/* Right Side: Company's Bank Details */}
            <div className="space-y-0.5 pl-2 leading-tight">
              <div className="text-center font-normal text-black mb-1">
                Company's Bank Details
              </div>
              <div className="grid grid-cols-[115px_1fr]">
                <span>A/c Holder's Name</span>
                <span>
                  : <strong>{bankAccountHolder}</strong>
                </span>
              </div>
              <div className="grid grid-cols-[115px_1fr]">
                <span>Bank Name</span>
                <span>
                  : <strong>{bankName}</strong>
                </span>
              </div>
              <div className="grid grid-cols-[115px_1fr]">
                <span>A/c No.</span>
                <span>
                  : <strong className="font-mono">{bankAccountNo}</strong>
                </span>
              </div>
              <div className="grid grid-cols-[115px_1fr]">
                <span>Branch & IFS Code</span>
                <span>: {bankBranchAndIfsc}</span>
              </div>
              <div className="grid grid-cols-[115px_1fr]">
                <span>SWIFT Code</span>
                <span>: {bankSwiftCode}</span>
              </div>
              {cleanUpiId && (
                <div className="grid grid-cols-[115px_1fr]">
                  <span>UPI ID</span>
                  <span>: <strong className="font-mono">{cleanUpiId}</strong></span>
                </div>
              )}
            </div>
          </div>

          {/* Lower Section: Declaration on Left | Authorised Signatory Box on Right */}
          <div className="grid grid-cols-2">
            {/* Left: Declaration */}
            <div className="p-2 flex flex-col justify-end">
              <div className="underline font-normal text-black mb-0.5">Declaration</div>
              <p className="text-[9.5px] leading-tight text-black max-w-[360px]">
                {raw.declaration ||
                  "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct."}
              </p>
            </div>

            {/* Right: Signature Box with top and left border */}
            <div
              onClick={onSignatureClick}
              className={`border-t border-l border-black p-2 flex flex-col justify-between min-h-[75px] print:min-h-[60px] text-right ${
                onSignatureClick ? "cursor-pointer group relative" : ""
              }`}
              title={onSignatureClick ? "Click to attach/change signature" : undefined}
            >
              <div className="font-bold text-[10px]">for {sellerName}</div>
              <div className="flex-1 flex items-center justify-end py-1">
                {activeSignature ? (
                  <img
                    src={activeSignature}
                    alt="Authorised Signatory"
                    className="max-h-12 max-w-[140px] object-contain"
                  />
                ) : (
                  <div className="h-10 print:h-8 flex items-center justify-center text-[9px] text-gray-500 text-center leading-tight">
                    This is computer generated invoice no signature required
                  </div>
                )}
              </div>
              <div className="font-normal text-[10px]">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>

      {/* Outside Footer */}
      <div className="text-center text-[10px] text-black mt-2 print:mt-1">
        This is computer generated invoice no signature required
      </div>
    </div>
  );
};

const EquityMartInvoiceTemplate = ({
  invoice,
  entityProfile,
  signatureUrl = null,
  onSignatureClick = null,
}) => {
  if (!invoice) return null;
  const raw = invoice.raw || {};
  const isIntraState = raw.isIntraState ?? (raw.buyerStateCode === "10" || raw.sellerStateCode === raw.buyerStateCode);

  const sellerAddress =
    raw.sellerAddress ||
    entityProfile?.officeAddress ||
    entityProfile?.OfficeAddress ||
    "503, A, Patliputra Mall, Block-B, Exhibition Road, Patna";
  const sellerGstin =
    raw.sellerGstin ||
    entityProfile?.gstin ||
    entityProfile?.Gstin ||
    "10BJKPR9978L1ZB";
  const sellerPhone =
    raw.sellerContact ||
    entityProfile?.contactPhone ||
    entityProfile?.contactNumber ||
    "9835284157";
  const sellerEmail =
    raw.sellerEmail ||
    entityProfile?.email ||
    entityProfile?.officialEmail ||
    "compliance@equityresearchmart.in";
  const sellerState = "10-Bihar";
  const sellerPan =
    raw.sellerPan ||
    entityProfile?.pan ||
    entityProfile?.Pan ||
    (sellerGstin && sellerGstin.length >= 12 ? sellerGstin.substring(2, 12) : "BJKPR9978L");
  const sebiReg = entityProfile?.sebiRegistrationNo || "INH000016700";

  const buyerName =
    invoice.customerName ||
    raw.buyerName ||
    "Customer";
  const buyerAddress =
    raw.buyerCity ||
    raw.buyerAddress ||
    "CUTTACK";
  const buyerContact =
    invoice.phone !== "N/A"
      ? invoice.phone
      : raw.buyerPhone || "N/A";
  const buyerPan = raw.buyerPan || "N/A";

  const invoiceNo = invoice.id || raw.invoiceNo || "1";
  const datedStr = formatSlashDate(invoice.date || raw.invoiceDate || new Date());

  let items = [];
  if (Array.isArray(invoice.items) && invoice.items.length > 0) {
    items = invoice.items;
  } else {
    const fallbackDisc = Number(invoice.discountAmount ?? invoice.discount ?? raw.discountAmount ?? raw.discount ?? 0);
    items = [
      {
        slNo: 1,
        description: invoice.service || invoice.serviceDescription || raw.serviceDescription || "Research Service Monthly Api",
        hsnSac: raw.primaryHsnSac || raw.hsnSac || invoice.hsnSac || "",
        serviceStartDate: invoice.serviceStartDate || raw.serviceStartDate,
        serviceEndDate: invoice.serviceEndDate || raw.serviceEndDate,
        quantity: raw.quantity || invoice.quantity || 1,
        rate: invoice.amount || raw.taxableAmount || raw.rate || 0,
        discountAmount: fallbackDisc,
        discountPercent: "",
        totalTaxAmount: invoice.gst || raw.totalTaxAmount || 0,
        totalItemAmount: invoice.total || raw.totalAmount || 0,
      },
    ];
  }

  const isGstBilling =
    invoice.isGstInvoice !== false &&
    raw.isGstInvoice !== false &&
    (Number(invoice.gst || raw.totalTaxAmount || 0) > 0 || invoice.isGstInvoice === true || raw.isGstInvoice === true);
  const isGstInvoice = isGstBilling;

  let totalDiscount = 0;
  let totalGst = 0;
  let totalAmount = 0;

  const processedItems = items.map((itm, idx) => {
    const qty = Number(itm.quantity ?? itm.Quantity) || 1;
    const rate = Number(itm.rate ?? itm.Rate) || 0;
    const disc = Number(itm.discountAmount ?? itm.DiscountAmount ?? itm.discount ?? itm.Discount) || 0;
    const taxable = Number(itm.taxableValue ?? itm.TaxableValue) || Math.max(0, rate * qty - disc);
    const tax = isGstInvoice
      ? (Number(itm.totalTaxAmount ?? itm.TotalTaxAmount) || Math.round(taxable * 0.18 * 100) / 100)
      : 0;
    const lineAmt =
      Number(itm.totalItemAmount ?? itm.TotalItemAmount ?? itm.amount ?? itm.Amount) ||
      Math.round((taxable + tax) * 100) / 100;

    totalDiscount += disc;
    totalGst += tax;
    totalAmount += lineAmt;

    const startDate = itm.serviceStartDate ?? itm.ServiceStartDate ?? invoice.serviceStartDate;
    const endDate = itm.serviceEndDate ?? itm.ServiceEndDate ?? invoice.serviceEndDate;
    const fromStr = startDate ? formatTallyDate(startDate) : "";
    const toStr = endDate ? formatTallyDate(endDate) : "";

    const discStr = disc > 0
      ? `₹ ${formatCurrency(disc)}${itm.discountPercent ? ` (${itm.discountPercent}%)` : ""}`
      : "0.00";

    const gstStr = isGstInvoice
      ? `₹ ${formatCurrency(tax)} (18%)`
      : "₹ 0.00 (0%)";

    return {
      sl: itm.slNo ?? itm.SlNo ?? idx + 1,
      description: itm.description || itm.Description || itm.serviceName || itm.service || "Research Service Monthly Api",
      hsnSac: itm.hsnSac || itm.HsnSac || raw.primaryHsnSac || raw.hsnSac || invoice.hsnSac || "",
      from: fromStr,
      to: toStr,
      qty,
      rate,
      discount: disc,
      discountText: discStr,
      gst: tax,
      gstText: gstStr,
      amount: lineAmt,
    };
  });

  if (totalDiscount === 0) {
    const invLevelDisc = Number(invoice.discountAmount ?? invoice.discount ?? raw.discountAmount ?? raw.discount ?? 0);
    if (invLevelDisc > 0) {
      totalDiscount = invLevelDisc;
      if (processedItems.length === 1) {
        processedItems[0].discount = invLevelDisc;
        processedItems[0].discountText = `₹ ${formatCurrency(invLevelDisc)}`;
      }
    }
  }

  const subTotal = totalAmount > 0 ? totalAmount : Number(invoice.total || 0);
  const roundedTotal = Math.round(subTotal);
  const roundOff = Math.round((roundedTotal - subTotal) * 100) / 100;
  const taxableVal = Math.max(0, subTotal - (isGstInvoice ? totalGst : 0));

  const cgstAmt = isGstInvoice ? (isIntraState ? Math.round((totalGst / 2) * 100) / 100 : 0) : 0;
  const sgstAmt = isGstInvoice ? (isIntraState ? Math.round((totalGst - cgstAmt) * 100) / 100 : 0) : 0;
  const igstAmt = isGstInvoice ? (!isIntraState ? totalGst : 0) : 0;

  const words = numberToWordsIndian(roundedTotal);
  const isCancelled = invoice.status === "Cancelled" || invoice.status === "Canceled" || raw.invoiceStatus === "Cancelled" || raw.invoiceStatus === "Canceled";
  const youSaved = totalDiscount;

  const activeSignature = raw.signatureUrl || signatureUrl || null;
  const notesText = raw.notes || "₹ 1,500 Maintenance Charge per Month payable every month";

  const bankAccountHolder =
    raw.bankAccountHolderName ||
    entityProfile?.bankAccountHolderName ||
    entityProfile?.BankAccountHolderName ||
    entityProfile?.companyName ||
    entityProfile?.CompanyName ||
    "EQUITY RESEARCH MART";

  const bankName =
    raw.bankName ||
    entityProfile?.bankName ||
    entityProfile?.BankName ||
    "YES BANK, PATNA, BIHAR";

  const bankAccountNo =
    raw.bankAccountNo ||
    entityProfile?.bankAccountNo ||
    entityProfile?.BankAccountNo ||
    "25361900008376";

  const bankBranchAndIfsc =
    raw.bankBranchAndIfsc ||
    entityProfile?.bankBranchAndIfsc ||
    entityProfile?.BankBranchAndIfsc ||
    "YESB0000253";

  const upiId =
    raw.upiId ||
    entityProfile?.upiId ||
    entityProfile?.UpiId ||
    entityProfile?.upi_id ||
    "yespay.bizsbiz118420@yesbankltd";
  const cleanUpiId = (upiId || "").trim();

  const payeeBusinessName = bankAccountHolder || "EQUITY RESEARCH MART";
  const upiGrandTotal = Number(roundedTotal || subTotal || invoice.total || raw.totalAmount || 0);
  const upiAmountStr = upiGrandTotal > 0 ? upiGrandTotal.toFixed(2) : "0.00";
  const upiIntentUri = `upi://pay?pa=${cleanUpiId}&pn=${encodeURIComponent(payeeBusinessName)}&am=${upiAmountStr}&cu=INR`;
  const dynamicQrSrc = getDynamicUpiQrSrc(upiIntentUri);

  return (
    <div className="invoice-page bg-white text-black p-3 sm:p-5 w-full max-w-[840px] mx-auto font-sans text-xs select-none">
      {/* Top Invoice Title */}
      <div className="text-center font-bold text-lg mb-2 text-gray-900 tracking-wide uppercase">
        {isGstBilling ? "TAX INVOICE" : "INVOICE"}
      </div>

      {/* Outer Bordered Box */}
      <div className="border border-black">
        {/* 1. Company Header */}
        <div className="p-3.5 border-b border-black">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <img
                src="/images/pdf_logo.png"
                alt="Equity Research Mart"
                className="w-20 h-20 object-contain shrink-0"
              />
              <div className="text-left space-y-0.5">
                <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none">
                  EQUITY RESEARCH MART
                </h1>
                <p className="text-[11px] text-gray-700 mt-1">{sellerAddress}</p>
                <p className="text-[11px] text-gray-800">
                  Phone: <strong className="font-bold text-black">{sellerPhone}</strong>
                </p>
                <p className="text-[11px] text-gray-800">
                  GSTIN: <strong className="font-bold text-black">{sellerGstin}</strong>
                </p>
                <p className="text-[11px] text-gray-800">
                  SEBI-Registered: <strong className="font-bold text-black">{sebiReg}</strong>
                </p>
              </div>
            </div>
            <div className="text-left text-[11px] space-y-1 shrink-0 w-[226px]">
              <div>
                Email: <strong className="font-medium text-black">{sellerEmail}</strong>
              </div>
              <div>
                State: <strong className="font-bold text-black">{sellerState}</strong>
              </div>
              <div>
                PAN: <strong className="font-bold text-black">{sellerPan}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Bill To & Invoice Details */}
        <div className="grid grid-cols-[1fr_240px] border-b border-black divide-x divide-black text-[11px]">
          <div className="text-left">
            <div className="bg-gray-50 border-b border-black px-2.5 py-1 font-bold text-gray-900">
              Bill To:
            </div>
            <div className="p-2.5 space-y-1">
              <div className="font-bold uppercase text-xs text-black">{buyerName}</div>
              <div className="text-gray-800 font-medium uppercase">{buyerAddress}</div>
              <div className="flex items-center gap-8 pt-1 text-[11px]">
                <div>
                  Contact No: <strong className="font-bold text-black">{buyerContact}</strong>
                </div>
                <div>
                  Pan No: <strong className="font-bold text-black">{buyerPan}</strong>
                </div>
              </div>
            </div>
          </div>
          <div className="text-left">
            <div className="bg-gray-50 border-b border-black px-2.5 py-1 font-bold text-gray-900">
              Invoice Details:
            </div>
            <div className="p-2.5 space-y-1.5">
              <div className="grid grid-cols-[85px_1fr]">
                <span>Invoice No.:</span>
                <strong className="font-bold text-black">{invoiceNo}</strong>
              </div>
              <div className="grid grid-cols-[85px_1fr]">
                <span>Date:</span>
                <strong className="font-bold text-black">{datedStr}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Particulars Table */}
        <div className="border-b border-black">
          <table className="w-full border-collapse text-[10px]">
            <thead className="bg-gray-50/50">
              <tr className="border-b border-black text-center font-bold divide-x divide-black text-gray-900">
                <th className="p-1.5 w-8">Sr.<br />No.</th>
                <th className="p-1.5 text-left">Product Description</th>
                <th className="p-1.5 w-16">HSN/<br />SAC</th>
                <th className="p-1.5 w-20">FROM</th>
                <th className="p-1.5 w-20">TO</th>
                <th className="p-1.5 w-14">Quantity</th>
                <th className="p-1.5 w-20">Price/<br />Unit(₹)</th>
                <th className="p-1.5 w-20">Discount(₹)</th>
                <th className="p-1.5 w-20">GST(₹)</th>
                <th className="p-1.5 w-24">Amount(₹)</th>
              </tr>
            </thead>
            <tbody>
              {processedItems.map((itm) => (
                <tr key={itm.sl} className="divide-x divide-black align-top">
                  <td className="p-1.5 text-center font-mono">{itm.sl}</td>
                  <td className="p-1.5 text-left font-bold text-black">
                    {itm.description}
                  </td>
                  <td className="p-1.5 text-center font-mono">{itm.hsnSac || "—"}</td>
                  <td className="p-1.5 text-center font-mono">{itm.from || "—"}</td>
                  <td className="p-1.5 text-center font-mono">{itm.to || "—"}</td>
                  <td className="p-1.5 text-center font-mono">{itm.qty}</td>
                  <td className="p-1.5 text-right font-mono">₹ {formatCurrency(itm.rate)}</td>
                  <td className="p-1.5 text-right font-mono">{itm.discountText}</td>
                  <td className="p-1.5 text-right font-mono">{itm.gstText}</td>
                  <td className="p-1.5 text-right font-mono font-bold">₹ {formatCurrency(itm.amount)}</td>
                </tr>
              ))}

              {/* Vertical spacing filler row to preserve proportion */}
              <tr className="h-28 divide-x divide-black">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>

              {/* Total Row */}
              <tr className="border-t border-black font-bold divide-x divide-black text-[11px] bg-gray-50/30">
                <td></td>
                <td className="p-1.5 text-left font-bold">Total</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="p-1.5 text-right font-mono">₹ {formatCurrency(totalDiscount)}</td>
                <td className="p-1.5 text-right font-mono">₹ {formatCurrency(totalGst)}</td>
                <td className="p-1.5 text-right font-mono font-bold">₹ {formatCurrency(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 4. Tax Summary & Payment Breakdown */}
        <div className="grid grid-cols-[1fr_310px] border-b border-black divide-x divide-black text-[10px]">
          {/* Left: Tax Summary */}
          <div className="text-left flex flex-col justify-between">
            <div>
              <div className="bg-gray-50 px-2.5 pt-2 pb-2.5 font-bold border-b border-black text-[11px]">
                Tax Summary:
              </div>

              {isIntraState ? (
                <div className="text-[9.5px]">
                  {/* Header Row */}
                  <div className="flex border-b border-black items-stretch text-center font-bold bg-gray-50/40">
                    <div className="w-[18%] border-r border-black flex items-center justify-center pt-2 pb-2.5 px-1 leading-tight">
                      HSN/ SAC
                    </div>
                    <div className="w-[22%] border-r border-black flex flex-col items-center justify-center pt-2 pb-2.5 px-1 leading-tight">
                      <span className="mb-0.5">Taxable</span>
                      <span>amount (₹)</span>
                    </div>
                    <div className="w-[21%] border-r border-black flex flex-col">
                      <div className="py-1 border-b border-black font-bold">CGST</div>
                      <div className="flex flex-1 items-center font-semibold text-[9px]">
                        <div className="w-[42%] pt-1 pb-1.5 border-r border-black">Rate (%)</div>
                        <div className="w-[58%] pt-1 pb-1.5">Amt (₹)</div>
                      </div>
                    </div>
                    <div className="w-[21%] border-r border-black flex flex-col">
                      <div className="py-1 border-b border-black font-bold">SGST</div>
                      <div className="flex flex-1 items-center font-semibold text-[9px]">
                        <div className="w-[42%] pt-1 pb-1.5 border-r border-black">Rate (%)</div>
                        <div className="w-[58%] pt-1 pb-1.5">Amt (₹)</div>
                      </div>
                    </div>
                    <div className="w-[18%] flex flex-col items-center justify-center pt-2 pb-2.5 px-1 leading-tight font-bold">
                      <span className="mb-0.5">Total</span>
                      <span>Tax (₹)</span>
                    </div>
                  </div>

                  {/* Data Row */}
                  <div className="flex border-b border-black items-center text-center font-mono text-[9.5px]">
                    <div className="w-[18%] border-r border-black pt-2 pb-2.5 px-1 text-center">
                      {processedItems[0]?.hsnSac || "998222"}
                    </div>
                    <div className="w-[22%] border-r border-black pt-2 pb-2.5 px-1.5 text-right">
                      {formatCurrency(taxableVal)}
                    </div>
                    <div className="w-[21%] border-r border-black flex items-center">
                      <div className="w-[42%] pt-2 pb-2.5 px-0.5 border-r border-black text-center">{isGstInvoice ? "9%" : "0%"}</div>
                      <div className="w-[58%] pt-2 pb-2.5 px-1 text-right">{formatCurrency(cgstAmt)}</div>
                    </div>
                    <div className="w-[21%] border-r border-black flex items-center">
                      <div className="w-[42%] pt-2 pb-2.5 px-0.5 border-r border-black text-center">{isGstInvoice ? "9%" : "0%"}</div>
                      <div className="w-[58%] pt-2 pb-2.5 px-1 text-right">{formatCurrency(sgstAmt)}</div>
                    </div>
                    <div className="w-[18%] pt-2 pb-2.5 px-1.5 text-right font-bold">
                      {formatCurrency(totalGst)}
                    </div>
                  </div>

                  {/* Total Row */}
                  <div className="flex items-center text-center font-bold font-mono text-[9.5px] bg-gray-50/30 border-b border-black">
                    <div className="w-[18%] border-r border-black pt-2.5 pb-3 px-1 text-center font-sans">
                      TOTAL
                    </div>
                    <div className="w-[22%] border-r border-black pt-2.5 pb-3 px-1.5 text-right">
                      {formatCurrency(taxableVal)}
                    </div>
                    <div className="w-[21%] border-r border-black flex items-center">
                      <div className="w-[42%] pt-2.5 pb-3 px-0.5 border-r border-black text-center"></div>
                      <div className="w-[58%] pt-2.5 pb-3 px-1 text-right">{formatCurrency(cgstAmt)}</div>
                    </div>
                    <div className="w-[21%] border-r border-black flex items-center">
                      <div className="w-[42%] pt-2.5 pb-3 px-0.5 border-r border-black text-center"></div>
                      <div className="w-[58%] pt-2.5 pb-3 px-1 text-right">{formatCurrency(sgstAmt)}</div>
                    </div>
                    <div className="w-[18%] pt-2.5 pb-3 px-1.5 text-right font-bold">
                      {formatCurrency(totalGst)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[9.5px]">
                  {/* Header Row */}
                  <div className="flex border-b border-black items-stretch text-center font-bold bg-gray-50/40">
                    <div className="w-[22%] border-r border-black flex items-center justify-center pt-2 pb-2.5 px-1 leading-tight">
                      HSN/ SAC
                    </div>
                    <div className="w-[28%] border-r border-black flex flex-col items-center justify-center pt-2 pb-2.5 px-1 leading-tight">
                      <span className="mb-0.5">Taxable</span>
                      <span>amount (₹)</span>
                    </div>
                    <div className="w-[28%] border-r border-black flex flex-col">
                      <div className="py-1 border-b border-black font-bold">IGST</div>
                      <div className="flex flex-1 items-center font-semibold text-[9px]">
                        <div className="w-[40%] pt-1 pb-1.5 border-r border-black">Rate (%)</div>
                        <div className="w-[60%] pt-1 pb-1.5">Amt (₹)</div>
                      </div>
                    </div>
                    <div className="w-[22%] flex flex-col items-center justify-center pt-2 pb-2.5 px-1 leading-tight font-bold">
                      <span className="mb-0.5">Total</span>
                      <span>Tax (₹)</span>
                    </div>
                  </div>

                  {/* Data Row */}
                  <div className="flex border-b border-black items-center text-center font-mono text-[9.5px]">
                    <div className="w-[22%] border-r border-black pt-2 pb-2.5 px-1.5 text-center">
                      {processedItems[0]?.hsnSac || "998222"}
                    </div>
                    <div className="w-[28%] border-r border-black pt-2 pb-2.5 px-1.5 text-right">
                      {formatCurrency(taxableVal)}
                    </div>
                    <div className="w-[28%] border-r border-black flex items-center">
                      <div className="w-[40%] pt-2 pb-2.5 px-0.5 border-r border-black text-center">{isGstInvoice ? "18%" : "0%"}</div>
                      <div className="w-[60%] pt-2 pb-2.5 px-1 text-right">{formatCurrency(igstAmt)}</div>
                    </div>
                    <div className="w-[22%] pt-2 pb-2.5 px-1.5 text-right font-bold">
                      {formatCurrency(totalGst)}
                    </div>
                  </div>

                  {/* Total Row */}
                  <div className="flex items-center text-center font-bold font-mono text-[9.5px] bg-gray-50/30 border-b border-black">
                    <div className="w-[22%] border-r border-black pt-2.5 pb-3 px-1.5 text-center font-sans">
                      TOTAL
                    </div>
                    <div className="w-[28%] border-r border-black pt-2.5 pb-3 px-1.5 text-right">
                      {formatCurrency(taxableVal)}
                    </div>
                    <div className="w-[28%] border-r border-black flex items-center">
                      <div className="w-[40%] pt-2.5 pb-3 px-0.5 border-r border-black text-center"></div>
                      <div className="w-[60%] pt-2.5 pb-3 px-1 text-right">{formatCurrency(igstAmt)}</div>
                    </div>
                    <div className="w-[22%] pt-2.5 pb-3 px-1.5 text-right font-bold">
                      {formatCurrency(totalGst)}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Fill any leftover vertical height cleanly */}
            <div className="flex-1 bg-white"></div>
          </div>

          {/* Right: Payment Details & Amount in Words */}
          <div className="divide-y divide-black text-[10.5px] text-left">
            <div className="p-1.5 space-y-1">
              <div className="grid grid-cols-[100px_20px_1fr] items-center">
                <span>Sub Total</span>
                <span>:</span>
                <span className="text-right font-mono font-bold">₹ {formatCurrency(subTotal)}</span>
              </div>
              <div className="grid grid-cols-[100px_20px_1fr] items-center">
                <span>Round Off</span>
                <span>:</span>
                <span className="text-right font-mono font-bold">₹ {formatCurrency(roundOff)}</span>
              </div>
              <div className="grid grid-cols-[100px_20px_1fr] items-center text-[11px] font-bold">
                <span>Total</span>
                <span>:</span>
                <span className="text-right font-mono font-bold text-xs">₹ {formatCurrency(roundedTotal)}</span>
              </div>
            </div>
            <div className="p-1.5">
              <div className="font-bold text-[10px] text-gray-800">Invoice Amount in Words:</div>
              <div className="font-medium text-gray-900 mt-0.5 leading-snug">{words}</div>
            </div>
            <div className="p-1.5 space-y-1">
              <div className="grid grid-cols-[100px_20px_1fr] items-center">
                <span>You Saved</span>
                <span>:</span>
                <span className="text-right font-mono font-bold">₹ {formatCurrency(youSaved)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Terms & Conditions */}
        <div className="p-2.5 border-b border-black text-[9.5px] leading-normal text-gray-800 text-left">
          <div className="font-bold text-black text-[10.5px] mb-1">Terms & Conditions:</div>
          <div className="space-y-1 text-gray-700">
            <div className="flex items-start gap-1.5 leading-snug">
              <span className="font-medium text-gray-800 shrink-0 select-none w-3.5 text-left">1.</span>
              <span className="flex-1">
                Investments in securities market are subject to market risks. Read all the related documents carefully before investing.
              </span>
            </div>
            <div className="flex items-start gap-1.5 leading-snug">
              <span className="font-medium text-gray-800 shrink-0 select-none w-3.5 text-left">2.</span>
              <span className="flex-1">
                Registration granted by SEBI, Enlistment as RA with Exchange and certification from NISM in no way guarantee performance of the intermediary or provide any assurance of returns to investors.
              </span>
            </div>
            <div className="flex items-start gap-1.5 leading-snug">
              <span className="font-medium text-gray-800 shrink-0 select-none w-3.5 text-left">3.</span>
              <span className="flex-1">Never Trade Without Stop Loss.</span>
            </div>
            <div className="flex items-start gap-1.5 leading-snug">
              <span className="font-medium text-gray-800 shrink-0 select-none w-3.5 text-left">4.</span>
              <span className="flex-1">
                Please read all terms and conditions and Refund Policy carefully on our website: www.equityresearchmart.in.
              </span>
            </div>
            <div className="flex items-start gap-1.5 leading-snug">
              <span className="font-medium text-gray-800 shrink-0 select-none w-3.5 text-left">5.</span>
              <span className="flex-1">
                If you have any queries concerning this invoice Contact us at : + 91 7004493381
              </span>
            </div>
          </div>
          <div className="pt-2 text-gray-900 font-semibold">Notes- {notesText}</div>
        </div>

        {/* 6. Bank Details & Signature */}
        <div className="grid grid-cols-[1fr_260px] divide-x divide-black text-[10.5px] text-left">
          <div className="p-2.5 flex items-start gap-3.5">
            <div className="flex flex-col items-center shrink-0">
              <img
                src={dynamicQrSrc}
                alt="UPI QR"
                className="w-20 h-20 object-contain rounded bg-white"
                title={upiIntentUri}
              />
              <div
                className="mt-1.5 mb-2.5 bg-emerald-600 text-white font-bold rounded tracking-tight text-center select-none"
                style={{
                  display: "inline-block",
                  fontSize: "8px",
                  lineHeight: "9px",
                  paddingTop: "2px",
                  paddingBottom: "9px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  marginBottom: "8px",
                  borderRadius: "3px",
                  backgroundColor: "#059669",
                  color: "#ffffff",
                  letterSpacing: "0.2px",
                }}
              >
                UPI SCAN TO PAY
              </div>
            </div>
            <div className="space-y-1 text-gray-800 text-[10.5px]">
              <div className="font-bold text-gray-900 text-[11px] mb-1">Bank Details:</div>
              <div>
                Name : <strong className="font-bold text-black">{bankName}</strong>
              </div>
              <div>
                Account No. : <strong className="font-bold text-black">{bankAccountNo}</strong>
              </div>
              <div>
                IFSC code : <strong className="font-bold text-black">{bankBranchAndIfsc}</strong>
              </div>
              <div>
                Account holder's name : <strong className="font-bold text-black">{payeeBusinessName}</strong>
              </div>
              {cleanUpiId && (
                <div>
                  UPI ID : <strong className="font-bold font-mono text-black">{cleanUpiId}</strong>
                </div>
              )}
            </div>
          </div>
          <div className="p-2.5 flex flex-col justify-between">
            <div className="font-bold text-gray-900 text-[11px]">For EQUITY RESEARCH MART:</div>
            <div
              className="h-20 flex flex-col justify-between items-center cursor-pointer relative"
              onClick={onSignatureClick}
              title="Click to change signature"
            >
              <div className="flex-1 w-full flex items-center justify-center">
                {activeSignature ? (
                  <img
                    src={activeSignature}
                    alt="Signature"
                    className="max-h-14 max-w-[140px] object-contain mb-1"
                  />
                ) : (
                  <div className="text-[8.5px] text-gray-500 italic text-center px-2 leading-tight select-none">
                    This is computer generated invoice no signature required
                  </div>
                )}
              </div>
              <div className="text-gray-900 font-medium text-[11px]">Authorized Signatory</div>
            </div>
          </div>
        </div>
      </div>

      {/* Outside Footer */}
      <div className="text-center text-[10px] text-gray-600 italic mt-2.5 print:mt-1.5">
        This is computer generated invoice no signature required
      </div>
    </div>
  );
};

const StatutoryInvoiceDoc = ({
  invoice,
  entityProfile,
  template = "equity_mart",
  signatureUrl = null,
  onSignatureClick = null,
}) => {
  if (!invoice) return null;
  if (template === "tally") {
    return (
      <TallyTaxInvoiceTemplate
        invoice={invoice}
        entityProfile={entityProfile}
        signatureUrl={signatureUrl}
        onSignatureClick={onSignatureClick}
      />
    );
  }
  return (
    <EquityMartInvoiceTemplate
      invoice={invoice}
      entityProfile={entityProfile}
      signatureUrl={signatureUrl}
      onSignatureClick={onSignatureClick}
    />
  );
};

const InvoicePanel = () => {
  const [currentEntityId, setCurrentEntityId] = useState(null);
  const [entityProfile, setEntityProfile] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(() => {
    try {
      return localStorage.getItem("EquityReaserchMart_invoice_signature") || null;
    } catch {
      return null;
    }
  });

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setSignatureUrl(dataUrl);
      try {
        localStorage.setItem("EquityReaserchMart_invoice_signature", dataUrl);
      } catch (err) {
        console.error("Failed to save signature to localStorage:", err);
      }
      toast.success("Signature attached successfully!");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setSignatureUrl(null);
    try {
      localStorage.removeItem("EquityReaserchMart_invoice_signature");
    } catch (err) {
      console.error("Failed to remove signature from localStorage:", err);
    }
    toast.info("Signature removed");
  };

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextInvoiceNo, setNextInvoiceNo] = useState("");

  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  const [quickFilter, setQuickFilter] = useState(null);

  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    statuses: [],
    service: "All",
    upiId: "All",
    rmName: "All",
    clientType: "All",
    minAmount: "",
    maxAmount: "",
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [parsedUploadRows, setParsedUploadRows] = useState([]);
  const [taxCalculationMode, setTaxCalculationMode] = useState("inclusive");
  const [rawWorkbookData, setRawWorkbookData] = useState([]);
  const [isBulkGstInvoice, setIsBulkGstInvoice] = useState(true);

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [bulkPrintInvoices, setBulkPrintInvoices] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("equity_mart");
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState("");
  const [generatingPdfInvoiceId, setGeneratingPdfInvoiceId] = useState(null);

  const dynamicAvailableServices = React.useMemo(() => {
    const serviceSet = new Set();
    invoices.forEach((inv) => {
      const raw = inv.raw || {};
      const desc =
        raw.serviceDescription ||
        inv.serviceDescription ||
        inv.service ||
        (inv.items && inv.items[0]?.service_name) ||
        (inv.items && inv.items[0]?.description);
      if (desc && typeof desc === "string" && desc.trim()) {
        serviceSet.add(desc.trim());
      }
    });
    const arr = Array.from(serviceSet);
    return arr.length > 0 ? arr : ["Services"];
  }, [invoices]);

  const dynamicAvailableUpiIds = React.useMemo(() => {
    const upiSet = new Set();
    invoices.forEach((inv) => {
      const upi = inv.upiId || inv.raw?.upiId || inv.raw?.UpiId;
      if (upi && typeof upi === "string" && upi.trim()) {
        upiSet.add(upi.trim());
      }
    });
    return Array.from(upiSet);
  }, [invoices]);

  const dynamicAvailableRms = React.useMemo(() => {
    const rmSet = new Set();
    invoices.forEach((inv) => {
      const rm = inv.rmName || inv.raw?.rmName;
      if (rm && typeof rm === "string" && rm.trim() && rm.trim() !== "N/A") {
        rmSet.add(rm.trim());
      }
    });
    return Array.from(rmSet);
  }, [invoices]);

  const collectAllCssStyles = () => {
    let css = "";
    try {
      const sheets = Array.from(document.styleSheets);
      for (const sheet of sheets) {
        try {
          const rules = sheet.cssRules || sheet.rules;
          if (rules) {
            for (let i = 0; i < rules.length; i++) {
              css += rules[i].cssText + "\n";
            }
          }
        } catch (e) {
          if (sheet.href) {
            css += `@import url("${sheet.href}");\n`;
          }
        }
      }
    } catch (err) {
      console.warn("Could not collect full stylesheet rules:", err);
    }
    return css;
  };

  const INVOICE_PDF_BASE_URL =
    process.env.REACT_APP_INVOICE_PDF_BASE_URL || BASE_URL;

  const getInvoicePdfUrl = (inv) => {
    if (!inv) return "";
    // Use invoiceNo for URL: {base}/invoices/{invoiceNo}.pdf
    // inv.id is already invoiceNo in mapped objects; inv.raw.invoiceNo for raw objects
    const invoiceNo = inv.raw?.invoiceNo || inv.invoiceNo || inv.id;
    if (invoiceNo) {
      return `${INVOICE_PDF_BASE_URL}/invoices/${invoiceNo}.pdf`;
    }
    return "";
  };

  const generatePdfBlobForInvoice = async (inv) => {
    const rawCustomerName =
      inv.customerName ||
      inv.raw?.buyerName ||
      inv.raw?.consigneeName ||
      "Customer";
    const filename = `${sanitizeFilename(inv.id || "INV")}_${sanitizeFilename(rawCustomerName)}.pdf`;

    // 1. Fetch pre-generated vector PDF directly from backend
    try {
      const pdfUrl = getInvoicePdfUrl(inv);
      if (pdfUrl) {
        const response = await fetch(pdfUrl, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const blob = await response.blob();
          return {
            blob,
            output: (type) => (type === "blob" ? blob : blob),
            save: (targetFilename) => saveAs(blob, targetFilename || filename),
          };
        }
      }
    } catch (directErr) {
      console.warn("Direct PDF fetch failed, trying on-demand generation:", directErr);
    }

    // 2. Try backend Chromium PDF generation from HTML
    try {
      const staticHtml = ReactDOMServer.renderToStaticMarkup(
        <div style={{ width: "800px", margin: "0 auto", padding: "10px", backgroundColor: "#ffffff", boxSizing: "border-box" }}>
          <StatutoryInvoiceDoc
            invoice={inv}
            entityProfile={entityProfile}
            template={selectedTemplate}
            signatureUrl={signatureUrl}
          />
        </div>
      );

      const allStyles = collectAllCssStyles();
      const fullDocumentHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <style>
    ${allStyles}
    @page {
      size: A4 portrait;
      margin: 4mm;
    }
    body {
      margin: 0;
      padding: 0;
      background-color: #ffffff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }
  </style>
</head>
<body>
  ${staticHtml}
</body>
</html>`;

      const response = await fetch(`${BASE_URL}/api/invoices/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          html: fullDocumentHtml,
          filename: filename,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        return {
          blob,
          output: (type) => (type === "blob" ? blob : blob),
          save: (targetFilename) => saveAs(blob, targetFilename || filename),
        };
      }
      console.warn("Backend PDF generation returned non-OK status:", response.status);
    } catch (backendErr) {
      console.warn("Backend PDF generation failed, falling back to client-side:", backendErr);
    }

    // 2. Client-side fallback via html2canvas & jsPDF
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "0px";
    container.style.top = "0px";
    container.style.width = "800px";
    container.style.padding = "0px";
    container.style.margin = "0px";
    container.style.backgroundColor = "#ffffff";
    container.style.zIndex = "-999";
    container.style.pointerEvents = "none";
    document.body.appendChild(container);

    const staticHtml = ReactDOMServer.renderToStaticMarkup(
      <div style={{ width: "800px", padding: "8px", backgroundColor: "#ffffff", boxSizing: "border-box" }}>
        <StatutoryInvoiceDoc
          invoice={inv}
          entityProfile={entityProfile}
          template={selectedTemplate}
          signatureUrl={signatureUrl}
        />
      </div>
    );
    container.innerHTML = staticHtml;

    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {}
    }
    await new Promise((resolve) => setTimeout(resolve, 150));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: 800,
    });

    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }

    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const margin = 5;
    const renderWidth = pageWidth - margin * 2;
    const renderHeight = (canvas.height * renderWidth) / canvas.width;

    pdf.addImage(imgData, "JPEG", margin, margin, renderWidth, Math.min(renderHeight, pageHeight - margin * 2));

    return pdf;
  };

  const handleDownloadInvoicesAsZip = async (invoiceList) => {
    if (!invoiceList || invoiceList.length === 0) {
      toast.warning("No invoices selected to download as ZIP.");
      return;
    }
    setIsGeneratingZip(true);
    setZipProgress(`Starting ZIP packaging for ${invoiceList.length} invoices...`);

    try {
      const zip = new JSZip();

      for (let i = 0; i < invoiceList.length; i++) {
        const inv = invoiceList[i];
        setZipProgress(`Rendering PDF ${i + 1} of ${invoiceList.length} (${inv.id || i + 1})...`);

        const pdf = await generatePdfBlobForInvoice(inv);
        const pdfBlob = pdf.output ? pdf.output("blob") : (pdf.blob || pdf);

        const rawCustomerName =
          inv.customerName ||
          inv.raw?.buyerName ||
          inv.raw?.consigneeName ||
          "Customer";
        const filename = `${sanitizeFilename(inv.id || `INV_${i + 1}`)}_${sanitizeFilename(rawCustomerName)}.pdf`;

        zip.file(filename, pdfBlob);
      }

      setZipProgress(`Compressing ${invoiceList.length} PDFs into ZIP archive...`);
      const zipBlob = await zip.generateAsync({ type: "blob" });

      const dateTag = new Date().toISOString().split("T")[0];
      const zipFilename = `Invoices_Batch_${invoiceList.length}_${dateTag}.zip`;
      saveAs(zipBlob, zipFilename);

      toast.success(`Successfully downloaded ${invoiceList.length} invoices as ${zipFilename}!`);
    } catch (err) {
      console.error("ZIP generation error:", err);
      toast.error("Failed to generate ZIP: " + (err?.message || "Unknown error"));
    } finally {
      setIsGeneratingZip(false);
      setZipProgress("");
    }
  };

  const handleDownloadSinglePdf = async (inv) => {
    if (!inv) return;
    const invoiceKey = inv.id || "invoice";
    setGeneratingPdfInvoiceId(invoiceKey);
    try {
      toast.info("Generating invoice PDF...");
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const pdf = await generatePdfBlobForInvoice(inv);

      const rawCustomerName =
        inv.customerName ||
        inv.raw?.buyerName ||
        inv.raw?.consigneeName ||
        "Customer";
      const filename = `${sanitizeFilename(inv.id || "INV")}_${sanitizeFilename(rawCustomerName)}.pdf`;

      pdf.save(filename);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to download PDF: " + (err?.message || "Unknown error"));
    } finally {
      setGeneratingPdfInvoiceId(null);
    }
  };

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    invoiceNo: "",
    customerName: "",
    email: "",
    phone: "",
    buyerPan: "",
    buyerAddress: "",
    buyerCity: "PATNA",
    buyerStateName: "Bihar",
    buyerStateCode: "10",
    consigneeSameAsBuyer: true,
    consigneeName: "",
    consigneeAddress: "",
    consigneeGstin: "",
    consigneeStateName: "Bihar",
    consigneeStateCode: "10",
    isGstInvoice: true,
    items: [
      {
        id: 1,
        service: "Research Services",
        hsnSac: "998222",
        serviceStartDate: new Date().toISOString().split("T")[0],
        serviceEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        quantity: 1,
        rate: "",
        discountAmount: 0,
      },
    ],
    service: "Research Services",
    serviceStartDate: new Date().toISOString().split("T")[0],
    serviceEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    hsnSac: "998222",
    amount: "",
    quantity: 1,
    taxRatePercent: 18,
    paymentMode: "UPI",
    modeOrTermsOfPayment: "UPI",
    destination: "PATNA",
    deliveryNote: "",
    dispatchDocNo: "",
    dispatchedThrough: "",
    date: new Date().toISOString().split("T")[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    status: INVOICE_STATUS_CONFIG[0]?.id || "Created",
    clientType: "New",
    rmName: "",
    upiId: "",
    notes: "",
  });

  const citiesForSelectedState = React.useMemo(() => {
    const code = formData.buyerStateCode || "10";
    const list = [];
    for (const [cityName, info] of Object.entries(INDIAN_CITY_STATE_MAP)) {
      if (info.code === code) {
        list.push(cityName);
      }
    }
    return list.sort();
  }, [formData.buyerStateCode]);

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          service: "",
          hsnSac: "998222",
          serviceStartDate: prev.date || new Date().toISOString().split("T")[0],
          serviceEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          quantity: 1,
          rate: "",
          discountAmount: 0,
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length <= 1) {
      toast.error("An invoice must contain at least one service item.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.items];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, items: updated };
    });
  };

  const formTotals = React.useMemo(() => {
    const isGst = !!formData.isGstInvoice;
    const taxRate = isGst ? (parseFloat(formData.taxRatePercent) || 18) : 0;
    let baseTaxable = 0;
    let totalDiscount = 0;

    (formData.items || []).forEach((item) => {
      const qty = parseFloat(item.quantity) || 1;
      const rate = parseFloat(item.rate) || 0;
      const disc = parseFloat(item.discountAmount) || 0;
      const itemTaxable = Math.max(0, rate * qty - disc);
      baseTaxable += itemTaxable;
      totalDiscount += disc;
    });

    const isIntraState = (formData.buyerStateCode || "10") === "10";
    const totalTax = isGst ? Math.round(((baseTaxable * taxRate) / 100) * 100) / 100 : 0;
    const cgst = isIntraState ? Math.round((totalTax / 2) * 100) / 100 : 0;
    const sgst = isIntraState ? Math.round((totalTax - cgst) * 100) / 100 : 0;
    const igst = isIntraState ? 0 : totalTax;
    const grandTotal = Math.round((baseTaxable + totalTax) * 100) / 100;

    return {
      baseTaxable,
      totalDiscount,
      taxRate,
      isIntraState,
      totalTax,
      cgst,
      sgst,
      igst,
      grandTotal,
    };
  }, [formData.items, formData.isGstInvoice, formData.taxRatePercent, formData.buyerStateCode]);

  const fetchEntityProfile = async (identifier = ENTITY_NAME) => {
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/profile?entity=${encodeURIComponent(identifier)}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        handleAuthError();
        return null;
      }
      const data = await res.json();
      if (data.success && data.payload) {
        setEntityProfile(data.payload);
        const resolvedId = data.payload.entityId;
        setCurrentEntityId(resolvedId);
        return resolvedId;
      }
    } catch (err) {
      console.warn("Could not fetch entity profile from backend:", err);
    }
    return null;
  };

  const fetchNextInvoiceNumber = async (entityId = currentEntityId) => {
    try {
      const url = entityId
        ? `${BASE_URL}/api/invoices/next-number?entity_id=${entityId}`
        : `${BASE_URL}/api/invoices/next-number?entity=${ENTITY_NAME}`;
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) return;
      const data = await res.json();
      if (data.success && data.payload?.nextInvoiceNumber) {
        setNextInvoiceNo(data.payload.nextInvoiceNumber);
      }
    } catch (err) {
      console.warn("Could not fetch sequence invoice number:", err);
    }
  };

  const fetchInvoices = async (entityId = currentEntityId) => {
    setIsLoading(true);
    try {
      const url = entityId
        ? `${BASE_URL}/api/invoices?entity_id=${entityId}&resultCount=100`
        : `${BASE_URL}/api/invoices?entity=${ENTITY_NAME}&resultCount=100`;
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        handleAuthError("Unauthorized! Please login with valid credentials to view invoices.");
        setIsLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success && data.payload?.invoices) {
        const mapped = data.payload.invoices.map((inv) => {
          let parsedItems = [];
          try {
            if (inv.itemsJson) {
              const rawParsed = JSON.parse(inv.itemsJson);
              if (Array.isArray(rawParsed)) {
                parsedItems = rawParsed.map((item) => ({
                  ...item,
                  discountAmount: Number(item.discountAmount ?? item.DiscountAmount ?? 0),
                  discountPercent: item.discountPercent ?? item.DiscountPercent ?? "",
                  serviceStartDate: item.serviceStartDate ?? item.ServiceStartDate,
                  serviceEndDate: item.serviceEndDate ?? item.ServiceEndDate,
                }));
              }
            }
          } catch (e) {
            parsedItems = [];
          }

          // Parse RM and Client Type from inv columns, falling back to otherReferences
          const otherRef = inv.otherReferences || "";
          const parsedRm = otherRef.match(/RM:\s*([^,]+)/i)?.[1]?.trim();
          const parsedClientType = otherRef.match(/Client Status:\s*([^,]+)/i)?.[1]?.trim();

          const clientType = inv.clientType || (parsedClientType === "Renwal" ? "Renewal" : parsedClientType) || "New";
          const rmName = inv.rmName || parsedRm || "N/A";
          const mail = (inv.buyerEmail && inv.buyerEmail !== "N/A") ? inv.buyerEmail : (inv.email || "N/A");

          return {
            id: inv.invoiceNo || `INV-${inv.id}`,
            rawId: inv.id,
            entityId: inv.entityId,
            customerName: inv.buyerName || "N/A",
            email: mail,
            phone: inv.buyerPhone || "N/A",
            clientType,
            rmName,
            mail,
            service: inv.serviceDescription || "Services",
            serviceStartDate: formatDateStr(inv.serviceStartDate),
            serviceEndDate: formatDateStr(inv.serviceEndDate),
            amount: inv.taxableAmount || 0,
            discountAmount: inv.discountAmount || 0,
            gst: inv.totalTaxAmount || 0,
            total: inv.totalAmount || 0,
            date: formatDateStr(inv.invoiceDate),
            dueDate: formatDateStr(inv.dueDate),
            status: inv.invoiceStatus || "Created",
            upiId: inv.upiId || inv.UpiId || "yespay.bizsbiz118420@yesbankltd",
            raw: inv,
            items: parsedItems,
          };
        });
        setInvoices(mapped);
      } else {
        setInvoices([]);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      toast.error("Failed to connect to invoice backend server.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const resolvedId = await fetchEntityProfile(ENTITY_NAME);
      const targetId = resolvedId || 2;
      fetchInvoices(targetId);
      fetchNextInvoiceNumber(targetId);
    };
    initializeData();
  }, []);

  const activeFiltersCount =
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0) +
    filters.statuses.length +
    (filters.service !== "All" ? 1 : 0) +
    (filters.upiId !== "All" ? 1 : 0) +
    (filters.rmName !== "All" ? 1 : 0) +
    (filters.clientType !== "All" ? 1 : 0) +
    (filters.minAmount ? 1 : 0) +
    (filters.maxAmount ? 1 : 0) +
    (quickFilter ? 1 : 0);

  const handleQuickFilterClick = (type) => {
    if (quickFilter === type) {
      setQuickFilter(null);
      setFilters((prev) => ({ ...prev, startDate: "", endDate: "" }));
      return;
    }

    setQuickFilter(type);
    const currentDate = new Date();

    if (type === "today") {
      const todayStr = formatDateStr(currentDate);
      setFilters((prev) => ({ ...prev, startDate: todayStr, endDate: todayStr }));
    } else if (type === "last7") {
      const past7 = new Date();
      past7.setDate(past7.getDate() - 7);
      setFilters((prev) => ({
        ...prev,
        startDate: formatDateStr(past7),
        endDate: formatDateStr(currentDate),
      }));
    } else if (type === "lastMonth") {
      const firstDayLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const lastDayLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      setFilters((prev) => ({
        ...prev,
        startDate: formatDateStr(firstDayLastMonth),
        endDate: formatDateStr(lastDayLastMonth),
      }));
    }
  };

  const handleStatusToggle = (statusId) => {
    setFilters((prev) => {
      const exists = prev.statuses.includes(statusId);
      return {
        ...prev,
        statuses: exists
          ? prev.statuses.filter((s) => s !== statusId)
          : [...prev.statuses, statusId],
      };
    });
  };

  const handleClearFilters = () => {
    setQuickFilter(null);
    setFilters({
      startDate: "",
      endDate: "",
      statuses: [],
      service: "All",
      upiId: "All",
      rmName: "All",
      clientType: "All",
      minAmount: "",
      maxAmount: "",
    });
    toast.info("Filters reset.");
  };

  const handleUpdateStatus = async (invoiceItem, newStatus) => {
    if (!invoiceItem?.rawId) {
      setInvoices((prev) =>
        prev.map((item) => (item.id === invoiceItem.id ? { ...item, status: newStatus } : item))
      );
      toast.success(`Invoice ${invoiceItem.id} status updated to "${newStatus}"`);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/invoices/${invoiceItem.rawId}/status`, {
        method: "PATCH",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.status === 401) {
        toast.error("Session expired or unauthorized. Please log in again.");
        return;
      }
      const data = await res.json();
      if (data.success) {
        setInvoices((prev) =>
          prev.map((item) => (item.rawId === invoiceItem.rawId ? { ...item, status: newStatus } : item))
        );
        toast.success(`Invoice ${invoiceItem.id} updated to "${newStatus}"`);
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Status update error:", err);
      toast.error("Failed to update status on server.");
    }
  };

  const handleCancelInvoice = (invoiceItem) => {
    if (!window.confirm(`Are you sure you want to cancel invoice ${invoiceItem.id}?`)) return;
    handleUpdateStatus(invoiceItem, "Cancelled");
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpdateClientType = async (inv, newType) => {
    if (!inv || !inv.rawId) return;
    try {
      const res = await fetch(`${BASE_URL}/api/invoices/${inv.rawId}/client-type`, {
        method: "PATCH",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ clientType: newType }),
      });
      if (res.status === 401) {
        toast.error("Session expired or unauthorized. Please log in again.");
        return;
      }
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Client type updated to "${newType}" for ${inv.id}`);
        setInvoices((prev) =>
          prev.map((i) => (i.id === inv.id ? { ...i, clientType: newType } : i))
        );
      } else {
        toast.error(data.message || "Failed to update client type.");
      }
    } catch (err) {
      console.error("Client type error:", err);
      toast.error("Error updating client type.");
    }
  };

  const filteredInvoices = invoices.filter((item) => {
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const matchesSearch =
        (item.id && item.id.toLowerCase().includes(q)) ||
        (item.customerName && item.customerName.toLowerCase().includes(q)) ||
        (item.email && item.email.toLowerCase().includes(q)) ||
        (item.phone && item.phone.toLowerCase().includes(q)) ||
        (item.rmName && item.rmName.toLowerCase().includes(q)) ||
        (item.clientType && item.clientType.toLowerCase().includes(q)) ||
        (item.status && item.status.toLowerCase().includes(q)) ||
        (item.upiId && item.upiId.toLowerCase().includes(q)) ||
        (item.service && item.service.toLowerCase().includes(q));

      if (!matchesSearch) return false;
    }

    if (filters.statuses && filters.statuses.length > 0) {
      const itemStatus = (item.status || "").trim().toLowerCase();
      const matched = filters.statuses.some((s) => s.trim().toLowerCase() === itemStatus);
      if (!matched) return false;
    }

    if (filters.startDate && item.date && item.date < filters.startDate) return false;
    if (filters.endDate && item.date && item.date > filters.endDate) return false;

    if (filters.service && filters.service !== "All") {
      const targetService = filters.service.trim().toLowerCase();
      const s1 = (item.service || "").trim().toLowerCase();
      const s2 = (item.raw?.serviceDescription || "").trim().toLowerCase();
      const s3 = (item.items?.[0]?.description || item.items?.[0]?.service_name || "").trim().toLowerCase();
      if (s1 !== targetService && s2 !== targetService && s3 !== targetService && !s1.includes(targetService) && !s2.includes(targetService)) {
        return false;
      }
    }

    if (filters.upiId && filters.upiId !== "All") {
      const targetUpi = filters.upiId.trim().toLowerCase();
      const itemUpi = (item.upiId || item.raw?.upiId || item.raw?.UpiId || "").trim().toLowerCase();
      if (!itemUpi.includes(targetUpi)) return false;
    }

    if (filters.rmName && filters.rmName !== "All") {
      const targetRm = filters.rmName.trim().toLowerCase();
      const itemRm = (item.rmName || item.raw?.rmName || "").trim().toLowerCase();
      if (!itemRm.includes(targetRm)) return false;
    }

    if (filters.clientType && filters.clientType !== "All") {
      const targetCt = filters.clientType.trim().toLowerCase();
      const itemCt = (item.clientType || item.raw?.clientType || "").trim().toLowerCase();
      if (itemCt !== targetCt) return false;
    }

    if (filters.minAmount && !isNaN(parseFloat(filters.minAmount))) {
      if (Number(item.total) < parseFloat(filters.minAmount)) return false;
    }
    if (filters.maxAmount && !isNaN(parseFloat(filters.maxAmount))) {
      if (Number(item.total) > parseFloat(filters.maxAmount)) return false;
    }

    return true;
  });

  const totalInvoicesCount = invoices.length;

  const toggleSelectAll = () => {
    if (selectedInvoiceIds.size === filteredInvoices.length && filteredInvoices.length > 0) {
      setSelectedInvoiceIds(new Set());
    } else {
      setSelectedInvoiceIds(new Set(filteredInvoices.map((i) => i.id)));
    }
  };

  const toggleSelectInvoice = (id) => {
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getField = (row, candidates, defaultValue = "") => {
    if (!row) return defaultValue;
    const rowKeys = Object.keys(row);
    // Tier 1: exact normalized match
    for (const cand of candidates) {
      const candNorm = cand.toLowerCase().replace(/[^a-z0-9]/g, "");
      for (const key of rowKeys) {
        const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (keyNorm === candNorm) {
          const val = row[key];
          if (val !== undefined && val !== null && String(val).trim() !== "") return val;
        }
      }
    }
    // Tier 2: startsWith match (e.g. "Client Name * [MANDATORY]" matches "clientname")
    for (const cand of candidates) {
      const candNorm = cand.toLowerCase().replace(/[^a-z0-9]/g, "");
      for (const key of rowKeys) {
        const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (keyNorm.startsWith(candNorm)) {
          const val = row[key];
          if (val !== undefined && val !== null && String(val).trim() !== "") return val;
        }
      }
    }
    // Tier 3: contains match
    for (const cand of candidates) {
      const candNorm = cand.toLowerCase().replace(/[^a-z0-9]/g, "");
      for (const key of rowKeys) {
        const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, "");
        if (keyNorm.includes(candNorm)) {
          const val = row[key];
          if (val !== undefined && val !== null && String(val).trim() !== "") return val;
        }
      }
    }
    return defaultValue;
  };

  const processExcelData = (rawRows, mode = "inclusive", isGst = true) => {
    if (!rawRows || rawRows.length === 0) return [];

    return rawRows.map((row, idx) => {
      // Resolve Fields
      const clientName = String(
        getField(row, ["client name", "customer name", "buyer name", "name"], "N/A")
      ).trim();

      const mobile = String(
        getField(row, ["mobile", "phone", "contact", "cell"], "")
      ).trim();

      const email = String(
        getField(row, ["email", "mail"], "")
      ).trim();

      const pan = String(
        getField(row, ["buyer pan", "client pan", "pan"], "")
      ).trim().toUpperCase();

      const city = String(
        getField(row, ["city", "town", "location"], "")
      ).trim();

      const stateRaw = String(
        getField(row, ["state", "buyer state", "state name", "client state"], "")
      ).trim();

      const paymentVal =
        parseFloat(getField(row, ["amount / fee", "amount/fee", "amount", "fee", "payment", "rate", "price", "total", "grand total"], 0)) || 0;

      const dateRaw = getField(row, ["date", "invoice date"], "");
      const dateVal = parseExcelDate(dateRaw) || new Date().toISOString().split("T")[0];

      // Auto-calculated & Optional Fields
      const bank = String(getField(row, ["bank", "bank name"], "Yes Bank")).trim();
      const refInvoiceNo = String(
        getField(row, ["invoice number", "invoice no", "inv no"], "")
      ).trim();
      const invoiceStatusRaw = String(
        getField(row, ["invoice status", "invoice", "status"], "")
      ).trim();
      const clientStatus = String(
        getField(row, ["client status", "client_status"], "New")
      ).trim();
      const rm = String(
        getField(row, ["rm", "relationship manager", "agent"], "")
      ).trim();
      // DO NOT consider "Amount in word" column from Excel even if it exists; calculate dynamically from total amount below.
      const serviceName = String(
        getField(row, ["service description", "service name", "service", "description", "package"], "Research Services")
      ).trim();

      const serviceStartDateRaw = getField(row, ["service start date", "start date"], "");
      const serviceStartDateVal = parseExcelDate(serviceStartDateRaw) || dateVal;

      const serviceEndDateRaw = getField(row, ["service end date", "end date"], "");
      let serviceEndDateVal = parseExcelDate(serviceEndDateRaw) || "";
      // Auto-compute service end date (+30 days) if omitted
      if (!serviceEndDateVal && serviceStartDateVal) {
        try {
          const sDate = new Date(serviceStartDateVal);
          if (!isNaN(sDate.getTime())) {
            const eDate = new Date(sDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            serviceEndDateVal = eDate.toISOString().split("T")[0];
          }
        } catch {
          // Keep blank if error
        }
      }

      // Resolve State and GST codes (Equity Research Mart is in 10-Bihar)
      const stateInfo = resolveStateFromCity(city, stateRaw);
      const isIntraState = stateInfo.code === "10";

      // Optional Dispatch & Billing overrides
      const buyerAddressRaw = String(
        getField(row, ["buyer address", "address", "customer address"], "")
      ).trim();
      const buyerAddress = buyerAddressRaw || (city ? `${city}, ${stateInfo.state}` : stateInfo.state);
      const buyerGstin = String(
        getField(row, ["buyer gstin", "gstin", "buyer gst"], "")
      ).trim().toUpperCase();
      const deliveryNote = String(getField(row, ["delivery note", "deliverynote"], "")).trim();
      const dispatchDocNo = String(getField(row, ["dispatch doc no", "dispatch doc"], "")).trim();
      const destination = String(getField(row, ["destination"], city || "")).trim();
      const termsOfPayment = String(
        getField(row, ["terms of payment", "terms of pay"], bank || "Immediate")
      ).trim();
      const termsOfDelivery = String(
        getField(row, ["terms of delivery", "delivery terms"], "Digital Delivery")
      ).trim();
      const upiId = String(
        getField(row, ["upi id", "upi_id", "upi", "vpa", "upi address"], "")
      ).trim();

      // Check for row-level GST Invoice override (e.g. Yes/No, true/false)
      const gstInvoiceRaw = String(
        getField(row, ["gst invoice", "gst invoice?", "is gst invoice", "tax invoice", "gst applicable", "gst"], "")
      ).trim().toLowerCase();

      let rowIsGst = isGst;
      if (
        gstInvoiceRaw === "no" ||
        gstInvoiceRaw === "false" ||
        gstInvoiceRaw === "without tax" ||
        gstInvoiceRaw === "0" ||
        gstInvoiceRaw === "exempt"
      ) {
        rowIsGst = false;
      } else if (
        gstInvoiceRaw === "yes" ||
        gstInvoiceRaw === "true" ||
        gstInvoiceRaw === "with tax" ||
        gstInvoiceRaw === "1" ||
        gstInvoiceRaw === "18%"
      ) {
        rowIsGst = true;
      }

      const discountVal = Math.max(0, parseFloat(getField(row, ["discount", "discount (₹)", "discount amount", "disc"], 0)) || 0);

      let taxable = 0;
      let totalTax = 0;
      let grandTotal = 0;

      if (!rowIsGst) {
        taxable = Math.max(0, paymentVal - discountVal);
        totalTax = 0;
        grandTotal = taxable;
      } else if (mode === "inclusive") {
        grandTotal = paymentVal;
        const grossTaxable = Math.round((paymentVal / 1.18) * 100) / 100;
        taxable = Math.max(0, grossTaxable - discountVal);
        totalTax = Math.round((grandTotal - taxable) * 100) / 100;
      } else {
        taxable = Math.max(0, paymentVal - discountVal);
        totalTax = Math.round((taxable * 0.18) * 100) / 100;
        grandTotal = Math.round((taxable + totalTax) * 100) / 100;
      }

      const cgst = rowIsGst && isIntraState ? Math.round((totalTax / 2) * 100) / 100 : 0;
      const sgst = rowIsGst && isIntraState ? Math.round((totalTax - cgst) * 100) / 100 : 0;
      const igst = rowIsGst && !isIntraState ? totalTax : 0;

      let status = "Created";
      const stLower = invoiceStatusRaw.toLowerCase();
      if (stLower === "sent") {
        status = "Send to Client";
      } else if (stLower === "paid" || stLower === "done" || stLower === "completed") {
        status = "Completed";
      } else if (stLower === "cancelled" || stLower === "canceled" || stLower === "cencelled") {
        status = "Cancelled";
      } else if (stLower === "na") {
        status = "Created";
      }

      // Due Date (optional - default invoice date + 7 days)
      const dueDateRaw = getField(row, ["due date", "due_date", "duedate"], "");
      let dueDateVal = parseExcelDate(dueDateRaw) || "";
      if (!dueDateVal && dateVal) {
        try {
          const idDate = new Date(dateVal);
          if (!isNaN(idDate.getTime())) {
            dueDateVal = new Date(idDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
          }
        } catch {
          // ignore
        }
      }

      // HSN (optional - default 998222)
      const hsnVal = String(getField(row, ["hsn", "sac", "hsn sac", "hsn/sac", "hsn code"], "998222")).trim() || "998222";

      // Payment Mode (mandatory, default "UPI")
      const paymentModeVal = String(
        getField(row, ["payment mode", "mode of payment", "pay mode", "payment type", "bank", "terms of payment"], "UPI")
      ).trim() || "UPI";

      return {
        sl: idx + 1,
        clientName,
        mobile,
        email,
        pan,
        city,
        stateInfo,
        isIntraState,
        isGstInvoice: rowIsGst,
        serviceName,
        serviceStartDateVal,
        serviceEndDateVal,
        dateVal,
        dueDateVal,
        paymentVal,
        paymentMode: paymentModeVal,
        hsnVal,
        discount: discountVal,
        taxable,
        totalTax,
        cgst,
        sgst,
        igst,
        grandTotal,
        bank,
        upiId,
        refInvoiceNo,
        clientStatus,
        rm,
        amountInWords: numberToWordsIndian(grandTotal),
        status,
        buyerAddress,
        buyerGstin,
        deliveryNote,
        dispatchDocNo,
        destination,
        termsOfPayment,
        termsOfDelivery,
      };
    });
  };

  const processUploadedFile = (file) => {
    if (!file) return;
    const validExtensions = [".xlsx", ".xls", ".csv"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validExtensions.includes(fileExt)) {
      toast.error("Please select a valid Excel or CSV file (.xlsx, .xls, .csv)");
      return;
    }
    setSelectedFile(file);
    setIsParsingFile(true);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!rawRows || rawRows.length === 0) {
          toast.error("The uploaded Excel sheet contains no rows.");
          return;
        }

        setRawWorkbookData(rawRows);
        const processed = processExcelData(rawRows, taxCalculationMode, isBulkGstInvoice);
        setParsedUploadRows(processed);
        toast.info(`Successfully parsed ${processed.length} invoices from ${file.name}`);
      } catch (err) {
        console.error("Excel parse error:", err);
        toast.error("Failed to parse Excel file. Please check structure.");
      } finally {
        setIsParsingFile(false);
      }
    };
    reader.onerror = () => {
      toast.error("Failed to read the file.");
      setIsParsingFile(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processUploadedFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
      processUploadedFile(e.dataTransfer.files[0]);
    }
  };

  const handleBulkGstToggle = (checked) => {
    setIsBulkGstInvoice(checked);
    if (rawWorkbookData && rawWorkbookData.length > 0) {
      const reprocessed = processExcelData(rawWorkbookData, taxCalculationMode, checked);
      setParsedUploadRows(reprocessed);
    }
  };

  const handleTaxModeChange = (newMode) => {
    setTaxCalculationMode(newMode);
    if (rawWorkbookData && rawWorkbookData.length > 0) {
      const reprocessed = processExcelData(rawWorkbookData, newMode, isBulkGstInvoice);
      setParsedUploadRows(reprocessed);
    }
  };

  const handleConfirmBulkUpload = async () => {
    if (!parsedUploadRows || parsedUploadRows.length === 0) {
      toast.error("No invoices available to import.");
      return;
    }

    setIsUploading(true);
    try {
      const targetEntityId = currentEntityId || 2;
      const bulkPayload = {
        entityId: targetEntityId,
        entityName: ENTITY_NAME,
        invoices: parsedUploadRows.map((p) => ({
          entityId: targetEntityId,
          entityName: ENTITY_NAME,
          buyerName: p.clientName,
          buyerPhone: p.mobile,
          buyerEmail: p.email,
          buyerPan: p.pan,
          buyerCity: p.city || null,
          buyerAddress: p.buyerAddress || (p.city ? `${p.city}, ${p.stateInfo.state}` : p.stateInfo.state),
          buyerGstin: p.buyerGstin || null,
          buyerStateName: p.stateInfo.state,
          buyerStateCode: p.stateInfo.code,
          invoiceNo: p.refInvoiceNo || null,
          deliveryNote: p.deliveryNote || null,
          dispatchDocNo: p.dispatchDocNo || null,
          destination: p.destination || p.city || null,
          paymentMode: p.paymentMode || "UPI",
          modeOrTermsOfPayment: p.paymentMode || p.termsOfPayment || "UPI",
          totalAmount: p.grandTotal,
          termsOfDelivery: p.termsOfDelivery || "Digital Delivery",
          invoiceDate:
            p.dateVal && !isNaN(new Date(p.dateVal).getTime())
              ? new Date(p.dateVal).toISOString()
              : new Date().toISOString(),
          dueDate:
            p.dueDateVal && !isNaN(new Date(p.dueDateVal).getTime())
              ? new Date(p.dueDateVal).toISOString()
              : (p.dateVal && !isNaN(new Date(p.dateVal).getTime())
                ? new Date(new Date(p.dateVal).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString()
                : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()),
          serviceDescription: p.serviceName,
          hsnSac: p.hsnVal || "998222",
          serviceStartDate:
            p.serviceStartDateVal && !isNaN(new Date(p.serviceStartDateVal).getTime())
              ? new Date(p.serviceStartDateVal).toISOString()
              : null,
          serviceEndDate:
            p.serviceEndDateVal && !isNaN(new Date(p.serviceEndDateVal).getTime())
              ? new Date(p.serviceEndDateVal).toISOString()
              : null,
          rate: p.paymentVal || p.taxable,
          discountAmount: p.discount || 0,
          quantity: 1,
          taxRatePercent: p.isGstInvoice !== undefined ? (p.isGstInvoice ? 18 : 0) : (isBulkGstInvoice ? 18 : 0),
          isGstInvoice: p.isGstInvoice !== undefined ? p.isGstInvoice : isBulkGstInvoice,
          referenceNoAndDate: p.refInvoiceNo ? `Invoice Ref: ${p.refInvoiceNo}` : "",
          otherReferences: `RM: ${p.rm || "N/A"}, Client Status: ${p.clientStatus || "N/A"}`,
          clientType: p.clientStatus === "Renwal" ? "Renewal" : (p.clientStatus || "New"),
          rmName: p.rm || null,
          upiId: p.upiId || null,
          invoiceStatus: p.status,
          notes: "",
        })),
      };

      const res = await fetch(`${BASE_URL}/api/invoices/bulk`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(bulkPayload),
      });

      if (res.status === 401) {
        toast.error("Session expired or unauthorized. Please log in again.");
        setIsUploading(false);
        return;
      }

      const data = await res.json();
      if (res.ok && data.success) {
        const createdCount = data.payload?.count ?? parsedUploadRows.length;
        const skippedCount = data.payload?.skippedCount ?? 0;
        if (createdCount > 0) {
          toast.success(
            `Successfully processed invoices: ${createdCount} created${
              skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ""
            }!`
          );
        } else if (skippedCount > 0) {
          toast.warning(`All ${skippedCount} invoice(s) were skipped as duplicates.`);
        } else {
          toast.info("Bulk invoice upload completed.");
        }
        setShowUploadModal(false);
        setSelectedFile(null);
        setParsedUploadRows([]);
        setRawWorkbookData([]);
        fetchInvoices(targetEntityId);
        fetchNextInvoiceNumber(targetEntityId);
      } else {
        toast.error(data.message || "Failed to import bulk invoices.");
      }
    } catch (err) {
      console.error("Bulk upload error:", err);
      toast.error("Network error during bulk invoice upload.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleExportInvoicesToExcel = (invoicesList, filename = "Statutory_Invoices_Export.xlsx") => {
    try {
      if (!invoicesList || invoicesList.length === 0) {
        toast.warning("No invoices available to export.");
        return;
      }

      const rows = invoicesList.map((inv, index) => {
        const isIntra = inv.raw?.isIntraState ?? (inv.raw?.buyerStateCode === "23");
        return {
          "Sl No": index + 1,
          "Invoice No": inv.id,
          "Invoice Date": inv.date || "",
          "Due Date": inv.dueDate || "",
          "Customer Name": inv.customerName || "",
          "Mobile": inv.phone !== "N/A" ? inv.phone : (inv.raw?.buyerPhone || ""),
          "Email": inv.email !== "N/A" ? inv.email : (inv.raw?.buyerEmail || ""),
          "PAN": inv.raw?.buyerPan || "",
          "Address / City": inv.raw?.buyerAddress || "",
          "Buyer State": inv.raw?.buyerStateName || "",
          "State Code": inv.raw?.buyerStateCode || "",
          "Service Description": inv.service || "",
          "Service Start Date": inv.serviceStartDate || "",
          "Service End Date": inv.serviceEndDate || "",
          "HSN / SAC": inv.raw?.primaryHsnSac || "998222",
          "Taxable Amount (₹)": Number(inv.amount || 0),
          "Tax Type": isIntra ? "Intra-State (CGST + SGST)" : "Inter-State (IGST)",
          "CGST Amount (₹)": Number(inv.raw?.cgstAmount || (isIntra ? inv.gst / 2 : 0)),
          "SGST Amount (₹)": Number(inv.raw?.sgstAmount || (isIntra ? inv.gst / 2 : 0)),
          "IGST Amount (₹)": Number(inv.raw?.igstAmount || (!isIntra ? inv.gst : 0)),
          "Total GST (₹)": Number(inv.gst || 0),
          "Total Amount (₹)": Number(inv.total || 0),
          "Status": inv.status || "Created",
          "Client Type": inv.clientType || "New",
          "RM": inv.rmName && inv.rmName !== "N/A" ? inv.rmName : "",
          "Payment Mode": inv.raw?.modeOrTermsOfPayment || "Online",
          "References / RM": inv.raw?.otherReferences || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = [
        { wch: 8 },  // Sl No
        { wch: 18 }, // Invoice No
        { wch: 14 }, // Invoice Date
        { wch: 14 }, // Due Date
        { wch: 26 }, // Customer Name
        { wch: 16 }, // Mobile
        { wch: 28 }, // Email
        { wch: 14 }, // PAN
        { wch: 28 }, // Address / City
        { wch: 18 }, // Buyer State
        { wch: 12 }, // State Code
        { wch: 32 }, // Service Description
        { wch: 16 }, // Service Start Date
        { wch: 16 }, // Service End Date
        { wch: 12 }, // HSN / SAC
        { wch: 18 }, // Taxable Amount
        { wch: 24 }, // Tax Type
        { wch: 16 }, // CGST Amount
        { wch: 16 }, // SGST Amount
        { wch: 16 }, // IGST Amount
        { wch: 16 }, // Total GST
        { wch: 18 }, // Total Amount
        { wch: 16 }, // Status
        { wch: 16 }, // Client Type
        { wch: 16 }, // RM
        { wch: 18 }, // Payment Mode
        { wch: 32 }, // References / RM
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GST_Invoices");
      XLSX.writeFile(workbook, filename);
      toast.success(`Successfully exported ${invoicesList.length} invoices to Excel!`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export invoices to Excel.");
    }
  };

  const handleDownloadSample = async () => {
    try {
      // 1. Try downloading server-generated styled template from backend
      try {
        const response = await fetch(`${BASE_URL}/api/invoices/sample-excel`);
        if (response.ok) {
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = "Invoice_Bulk_Upload_Sample.xlsx";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
          toast.success("Sample Excel template downloaded successfully!");
          return;
        }
      } catch (fetchErr) {
        console.warn("Backend sample excel fetch failed, generating client-side fallback:", fetchErr);
      }

      // 2. Client-side fallback generation
      // Sheet 1: Exact 17 columns requested (16 mandatory + 1 optional invoice number)
      const sampleRows = [
        {
          "Client Name": "MAYADHAR BEHERA",
          "Mobile": "9835284157",
          "Email": "mayadhar@gmail.com",
          "Pan": "AUHPB4503G",
          "City": "PATNA",
          "Date": "03/09/2026",
          "Payment": 13805.00,
          "Bank": "YES BANK",
          "Invoice Number": "220926001",
          "Invoice": "Completed",
          "Client status": "New",
          "Service Start date": "04-Aug-2026",
          "Service End date": "22-Aug-2026",
          "RM": "Rahul Sharma",
          "Amount in word": "Thirteen Thousand Eight Hundred Five Rupees only",
          "Service Name": "Research Service Monthly Api"
        },
        {
          "Client Name": "SIMA HOTA",
          "Mobile": "7978723389",
          "Email": "hottassima@gmail.com",
          "Pan": "ALYPH5449R",
          "City": "CUTTACK",
          "Date": "03/09/2026",
          "Payment": 11800.00,
          "Bank": "YES BANK",
          "Invoice Number": "", // Blank -> Auto-assigned by system
          "Invoice": "Completed",
          "Client status": "Renewal",
          "Service Start date": "03-Sep-2026",
          "Service End date": "02-Oct-2026",
          "RM": "Pooja Singh",
          "Amount in word": "Eleven Thousand Eight Hundred Rupees only",
          "Service Name": "Equity Premium Advisory"
        },
        {
          "Client Name": "RAJESH AGRAWAL",
          "Mobile": "9826012345",
          "Email": "rajesh.agrawal@outlook.com",
          "Pan": "BKPPB1234D",
          "City": "MUMBAI",
          "Date": "04/09/2026",
          "Payment": 25000.00,
          "Bank": "YES BANK",
          "Invoice Number": "", // Blank -> Auto-assigned by system
          "Invoice": "Completed",
          "Client status": "New",
          "Service Start date": "04-Sep-2026",
          "Service End date": "03-Dec-2026",
          "RM": "Amit Kumar",
          "Amount in word": "Twenty Five Thousand Rupees only",
          "Service Name": "HNI Research Quarterly"
        }
      ];

      // Sheet 2: Field Guide
      const guideRows = [
        { "Field Name": "Client Name", "Requirement": "MANDATORY", "Description": "Full legal name of customer/buyer billed on the statutory Tax Invoice." },
        { "Field Name": "Mobile", "Requirement": "MANDATORY", "Description": "10-digit primary mobile contact number of buyer." },
        { "Field Name": "Email", "Requirement": "MANDATORY", "Description": "Primary email address of the buyer." },
        { "Field Name": "Pan", "Requirement": "MANDATORY", "Description": "10-character Permanent Account Number (PAN) of buyer." },
        { "Field Name": "City", "Requirement": "MANDATORY", "Description": "Customer City. Auto-determines State and GST code (e.g. Patna -> Bihar 10; Cuttack -> Odisha 21)." },
        { "Field Name": "Date", "Requirement": "MANDATORY", "Description": "Invoice Date (DD/MM/YYYY or YYYY-MM-DD)." },
        { "Field Name": "Payment", "Requirement": "MANDATORY", "Description": "Total payment amount received in INR (₹)." },
        { "Field Name": "Bank", "Requirement": "MANDATORY", "Description": "Bank name or payment method (e.g. YES BANK)." },
        { "Field Name": "Invoice Number", "Requirement": "OPTIONAL", "Description": "Leave blank to auto-generate statutory scheme (DDMMYY + Serial No). Or provide manual override." },
        { "Field Name": "Invoice", "Requirement": "MANDATORY", "Description": "Invoice status ('Completed', 'Send to Client', 'Created')." },
        { "Field Name": "Client status", "Requirement": "MANDATORY", "Description": "Client onboarding status ('New' or 'Renewal')." },
        { "Field Name": "Service Start date", "Requirement": "MANDATORY", "Description": "Service / subscription start date." },
        { "Field Name": "Service End date", "Requirement": "MANDATORY", "Description": "Service / subscription expiry date." },
        { "Field Name": "RM", "Requirement": "MANDATORY", "Description": "Relationship Manager / Sales agent name." },
        { "Field Name": "Amount in word", "Requirement": "MANDATORY", "Description": "Amount written in words. If left blank, auto-calculated." },
        { "Field Name": "Service Name", "Requirement": "MANDATORY", "Description": "Research service package name appearing in invoice line items." }
      ];

      // Build Sheet 1
      const wsData = XLSX.utils.json_to_sheet(sampleRows);
      wsData["!cols"] = [
        { wch: 22 }, // Client Name
        { wch: 16 }, // Mobile
        { wch: 26 }, // Email
        { wch: 14 }, // Pan
        { wch: 16 }, // City
        { wch: 14 }, // Date
        { wch: 16 }, // Payment
        { wch: 16 }, // Bank
        { wch: 18 }, // Invoice Number
        { wch: 16 }, // Invoice
        { wch: 16 }, // Client status
        { wch: 18 }, // Service Start date
        { wch: 18 }, // Service End date
        { wch: 18 }, // RM
        { wch: 42 }, // Amount in word
        { wch: 28 }, // Service Name
      ];

      // Build Sheet 2
      const wsGuide = XLSX.utils.json_to_sheet(guideRows);
      wsGuide["!cols"] = [
        { wch: 22 }, // Field Name
        { wch: 16 }, // Requirement
        { wch: 65 }, // Description
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, wsData, "Invoices");
      XLSX.utils.book_append_sheet(workbook, wsGuide, "Field Guide");
      XLSX.writeFile(workbook, "Invoice_Bulk_Upload_Sample.xlsx");
      toast.success("Sample Excel (.xlsx) downloaded successfully!");
    } catch (err) {
      console.error("Download sample error:", err);
      toast.info("Sample template download initiated.");
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "buyerStateName") {
      const matched = INDIAN_STATES_LIST.find(
        (s) => s.name.toLowerCase() === (value || "").toLowerCase()
      );
      const code = matched ? matched.code : "";
      setFormData((prev) => ({
        ...prev,
        buyerStateName: value,
        buyerStateCode: code,
        ...(prev.consigneeSameAsBuyer
          ? {
              consigneeStateName: value,
              consigneeStateCode: code,
            }
          : {}),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateInvoiceSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerName.trim()) {
      toast.error("Please fill in the customer / company name.");
      return;
    }

    if (!formData.items || formData.items.length === 0) {
      toast.error("Please add at least one service item.");
      return;
    }

    for (let i = 0; i < formData.items.length; i++) {
      const itm = formData.items[i];
      if (!itm.service || !itm.service.trim()) {
        toast.error(`Please provide a description for service item #${i + 1}.`);
        return;
      }
      if (itm.rate === "" || isNaN(parseFloat(itm.rate)) || parseFloat(itm.rate) < 0) {
        toast.error(`Please enter a valid price for service item #${i + 1}.`);
        return;
      }
    }

    const isGst = !!formData.isGstInvoice;
    const taxRate = isGst ? (parseFloat(formData.taxRatePercent) || 18) : 0;
    const isIntraState = (formData.buyerStateCode || "23") === "23";

    const itemsPayload = formData.items.map((itm, idx) => {
      const qty = parseFloat(itm.quantity) || 1;
      const rate = parseFloat(itm.rate) || 0;
      const disc = parseFloat(itm.discountAmount) || 0;
      const taxable = Math.max(0, rate * qty - disc);
      const itmTax = isGst ? Math.round(((taxable * taxRate) / 100) * 100) / 100 : 0;
      const itmCgst = isIntraState ? Math.round((itmTax / 2) * 100) / 100 : 0;
      const itmSgst = isIntraState ? Math.round((itmTax - itmCgst) * 100) / 100 : 0;
      const itmIgst = isIntraState ? 0 : itmTax;

      return {
        slNo: idx + 1,
        description: itm.service.trim(),
        hsnSac: itm.hsnSac?.trim() || "998222",
        quantity: qty,
        unit: "Services",
        rate: rate,
        amount: Math.round(rate * qty * 100) / 100,
        taxableValue: taxable,
        cgstRate: isIntraState ? taxRate / 2 : 0,
        cgstAmount: itmCgst,
        sgstRate: isIntraState ? taxRate / 2 : 0,
        sgstAmount: itmSgst,
        igstRate: !isIntraState ? taxRate : 0,
        igstAmount: itmIgst,
        totalTaxAmount: itmTax,
        totalItemAmount: Math.round((taxable + itmTax) * 100) / 100,
        serviceStartDate: itm.serviceStartDate ? new Date(itm.serviceStartDate).toISOString() : null,
        serviceEndDate: itm.serviceEndDate ? new Date(itm.serviceEndDate).toISOString() : null,
        discountAmount: disc,
      };
    });

    const firstItem = itemsPayload[0] || {};
    const serviceDescriptionJoined = itemsPayload.map((i) => i.description).join(", ");
    const totalQty = itemsPayload.reduce((sum, itm) => sum + (itm.quantity || 1), 0);

    const targetId = currentEntityId || 2;
    const payload = {
      entityId: targetId,
      entityName: ENTITY_NAME,
      invoiceDate: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
      invoiceStatus: formData.status || "Created",
      currency: "INR",
      buyerName: formData.customerName.trim(),
      buyerAddress: formData.buyerAddress ? formData.buyerAddress.trim() : "Office / Residence Address N/A",
      buyerPan: formData.panno ? formData.panno.trim() : null,
      buyerStateName: formData.buyerStateName || "Madhya Pradesh",
      buyerStateCode: formData.buyerStateCode || "23",
      buyerEmail: formData.email ? formData.email.trim() : null,
      buyerPhone: formData.phone ? formData.phone.trim() : null,
      consigneeName: formData.consigneeSameAsBuyer
        ? formData.customerName.trim()
        : formData.consigneeName?.trim() || formData.customerName.trim(),
      consigneeAddress: formData.consigneeSameAsBuyer
        ? formData.buyerAddress?.trim() || "Same as Billing"
        : formData.consigneeAddress?.trim() || formData.buyerAddress?.trim(),
      consigneeGstin: formData.consigneeSameAsBuyer
        ? formData.panno?.trim() || null
        : formData.consigneeGstin?.trim() || null,
      consigneeStateName: formData.consigneeSameAsBuyer
        ? formData.buyerStateName
        : formData.consigneeStateName || "Madhya Pradesh",
      consigneeStateCode: formData.consigneeSameAsBuyer
        ? formData.buyerStateCode
        : formData.consigneeStateCode || "23",
      items: itemsPayload,
      serviceDescription: serviceDescriptionJoined || "Services",
      serviceStartDate: firstItem.serviceStartDate || (formData.date ? new Date(formData.date).toISOString() : null),
      serviceEndDate: firstItem.serviceEndDate || null,
      hsnSac: firstItem.hsnSac || "998222",
      quantity: totalQty,
      rate: firstItem.rate || formTotals.baseTaxable,
      discountAmount: formTotals.totalDiscount,
      taxRatePercent: taxRate,
      isGstInvoice: isGst,
      modeOrTermsOfPayment: formData.modeOrTermsOfPayment || "Online",
      destination: formData.destination || "Indore",
      deliveryNote: formData.deliveryNote || "",
      dispatchDocNo: formData.dispatchDocNo || "",
      dispatchedThrough: formData.dispatchedThrough || "",
      notes: formData.notes || "",
    };

    setIsCreatingInvoice(true);
    try {
      const res = await fetch(`${BASE_URL}/api/invoices`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        toast.error("Session expired or unauthorized. Please log in again.");
        return;
      }
      const data = await res.json();
      if (res.ok && data.success && data.payload) {
        const createdInvoiceNo = data.payload.invoice?.invoiceNo || data.payload.invoiceNo || "";
        toast.success(`Tax Invoice ${createdInvoiceNo} created successfully!`);
        setShowCreateModal(false);
        fetchInvoices(targetId);
        fetchNextInvoiceNumber(targetId);

        setFormData({
          customerName: "",
          email: "",
          phone: "",
          buyerAddress: "",
          panno: "",
          buyerStateName: "Madhya Pradesh",
          buyerStateCode: "23",
          consigneeSameAsBuyer: true,
          consigneeName: "",
          consigneeAddress: "",
          consigneeGstin: "",
          consigneeStateName: "Madhya Pradesh",
          consigneeStateCode: "23",
          isGstInvoice: true,
          items: [
            {
              id: 1,
              service: "Services",
              hsnSac: "998222",
              serviceStartDate: new Date().toISOString().split("T")[0],
              serviceEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
              quantity: 1,
              rate: "",
              discountAmount: 0,
            },
          ],
          service: "Services",
          serviceStartDate: new Date().toISOString().split("T")[0],
          serviceEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          hsnSac: "998222",
          amount: "",
          quantity: 1,
          taxRatePercent: 18,
          modeOrTermsOfPayment: "Online",
          destination: "Indore",
          deliveryNote: "",
          dispatchDocNo: "",
          dispatchedThrough: "",
          date: new Date().toISOString().split("T")[0],
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          status: INVOICE_STATUS_CONFIG[0]?.id || "Created",
          notes: "",
        });
      } else {
        toast.error(data.message || "Failed to create invoice");
      }
    } catch (err) {
      console.error("Create invoice error:", err);
      toast.error("Error creating invoice on server.");
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  const getStatusConfig = (statusId) => {
    return (
      INVOICE_STATUS_CONFIG.find((s) => s.id === statusId) || {
        id: statusId,
        label: statusId,
        description: "",
        badgeBg: "bg-gray-50",
        badgeText: "text-gray-700",
        badgeBorder: "border-gray-200",
        icon: "clock",
      }
    );
  };

  const renderStatusBadge = (statusId) => {
    const config = getStatusConfig(statusId);
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.badgeBg} ${config.badgeText} border ${config.badgeBorder}`}
      >
        {config.icon === "check-circle" && <FaCheckCircle className="text-[10px]" />}
        {config.icon === "paper-plane" && <FaPaperPlane className="text-[10px]" />}
        {config.icon === "clock" && <FaClock className="text-[10px]" />}
        {config.icon === "times-circle" && <FaTimesCircle className="text-[10px]" />}
        <span>{config.label}</span>
      </span>
    );
  };


  return (
    <div className="p-3 sm:p-4 md:p-8 pb-32 sm:pb-48 bg-gray-50 min-h-screen">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 0mm !important;
        }
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden !important;
          }
          #bulk-print-root, #bulk-print-root *,
          #single-print-root, #single-print-root * {
            visibility: visible !important;
          }
          #bulk-print-root, #single-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          /* Single print: strictly avoid forced break to prevent duplicate/2nd page */
          #single-print-root .invoice-page {
            page-break-before: avoid !important;
            page-break-after: avoid !important;
            break-before: avoid !important;
            break-after: avoid !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 5mm 8mm !important;
            box-sizing: border-box !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          /* Bulk print: break page between invoices, avoiding break after the last one */
          #bulk-print-root .invoice-page {
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin: 0 !important;
            padding: 5mm 8mm !important;
            box-sizing: border-box !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          #bulk-print-root .invoice-page:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          .print-hidden, .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <div className="flex items-center gap-2.5 sm:gap-3">
               
              {isLoading && (
                <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin shrink-0"></span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Statutory GST invoicing, sequential numbering, and tax compliance
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                const targetId = currentEntityId || 2;
                fetchInvoices(targetId);
                fetchEntityProfile(ENTITY_NAME);
                fetchNextInvoiceNumber(targetId);
                toast.info("Invoice registry refreshed.");
              }}
              className="p-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border border-gray-300 shadow-xs transition-all cursor-pointer"
              title="Refresh Invoices"
            >
              <FaSyncAlt className={`text-xs ${isLoading ? "animate-spin" : ""}`} />
            </button>

            <button
              type="button"
              onClick={handleDownloadSample}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 bg-white hover:bg-gray-50 text-gray-700 text-xs sm:text-sm font-medium rounded-lg border border-gray-300 shadow-xs transition-all cursor-pointer"
              title="Download client data excel template (.xlsx)"
            >
              <FaDownload className="text-emerald-600 text-xs" />
              <span>Sample Excel</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setParsedUploadRows([]);
                setRawWorkbookData([]);
                setShowUploadModal(true);
              }}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 py-1.5 sm:px-4 sm:py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-medium rounded-lg shadow-xs transition-all cursor-pointer"
              title="Upload client data Excel file and bulk generate invoices"
            >
              <FaFileExcel className="text-xs sm:text-sm" />
              <span>Bulk Upload</span>
            </button>

            <button
              type="button"
              onClick={() => handleExportInvoicesToExcel(filteredInvoices, `Invoices_Export_${filteredInvoices.length}.xlsx`)}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 bg-white hover:bg-gray-50 text-gray-700 text-xs sm:text-sm font-medium rounded-lg border border-gray-300 shadow-xs transition-all cursor-pointer"
              title="Export all displayed invoices to Excel (.xlsx)"
            >
              <FaFileExcel className="text-emerald-600 text-xs" />
              <span className="hidden sm:inline">Export</span> Excel
            </button>

            <button
              type="button"
              onClick={() => handleDownloadInvoicesAsZip(filteredInvoices)}
              disabled={isGeneratingZip}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs sm:text-sm font-medium rounded-lg border border-purple-200 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              title="Download all displayed invoices as individual PDFs inside a ZIP archive"
            >
              {isGeneratingZip ? (
                <FaSpinner className="animate-spin text-purple-600 text-xs" />
              ) : (
                <FaFileArchive className="text-purple-600 text-xs" />
              )}
              <span className="hidden sm:inline">Bulk</span> Download (ZIP)
            </button>

            <button
              type="button"
              onClick={() => {
                fetchNextInvoiceNumber(currentEntityId || 2);
                setShowCreateModal(true);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3.5 py-2 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg shadow-xs transition-all cursor-pointer"
            >
              <FaPlus className="text-xs" />
              <span>Create New Invoice</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Total Invoices
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{totalInvoicesCount}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  All registered invoices
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <FaFileInvoice size={18} />
              </div>
            </div>
          </div>

          {INVOICE_STATUS_CONFIG.map((statusItem) => {
            const count = invoices.filter((i) => i.status === statusItem.id).length;
            return (
              <div key={statusItem.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 truncate">
                      {statusItem.label}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${statusItem.badgeText}`}>{count}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{statusItem.description}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusItem.badgeBg} ${statusItem.badgeText}`}>
                    {statusItem.icon === "check-circle" && <FaCheckCircle size={18} />}
                    {statusItem.icon === "paper-plane" && <FaPaperPlane size={16} />}
                    {statusItem.icon === "clock" && <FaClock size={18} />}
                    {statusItem.icon === "times-circle" && <FaTimesCircle size={18} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all">
          <div className="p-4 flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-3 w-full lg:w-auto flex-1 max-w-xl">
              <div className="relative flex-1">
                <FaSearch className="absolute left-3.5 top-3 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search invoice #, customer, email, or service..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                  showFilterPanel || activeFiltersCount > 0
                    ? "bg-blue-50 text-blue-700 border-blue-300 ring-2 ring-blue-100"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <FaFilter className={`text-xs ${activeFiltersCount > 0 ? "text-blue-600" : "text-gray-500"}`} />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
                {showFilterPanel ? (
                  <FaChevronUp className="text-[10px] text-gray-400" />
                ) : (
                  <FaChevronDown className="text-[10px] text-gray-400" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-2 w-full lg:w-auto justify-between lg:justify-end overflow-x-auto pb-1 lg:pb-0">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 mr-1 whitespace-nowrap hidden sm:inline">
                Quick Filters:
              </span>

              <button
                type="button"
                onClick={() => handleQuickFilterClick("today")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                  quickFilter === "today"
                    ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                Today
              </button>

              <button
                type="button"
                onClick={() => handleQuickFilterClick("last7")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                  quickFilter === "last7"
                    ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                Last 7 Days
              </button>

              <button
                type="button"
                onClick={() => handleQuickFilterClick("lastMonth")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                  quickFilter === "lastMonth"
                    ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                Last Month
              </button>

              {quickFilter && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 cursor-pointer"
                  title="Clear Quick Filter"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>
          </div>

          {showFilterPanel && (
            <div className="p-5 border-t border-gray-100 bg-gray-50/70 space-y-4 animate-fadeIn">
              <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <FaFilter className="text-blue-600 text-xs" />
                  <span>Filter Invoices by Multiple Criteria</span>
                </div>
                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1 cursor-pointer"
                  >
                    <FaUndo className="text-[10px]" />
                    <span>Reset All</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Start Date */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5 flex items-center gap-1.5">
                    <FaCalendarAlt className="text-gray-400 text-xs" />
                    <span>Start Date</span>
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => {
                      setQuickFilter(null);
                      setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                    }}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 2. End Date */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5 flex items-center gap-1.5">
                    <FaCalendarAlt className="text-gray-400 text-xs" />
                    <span>End Date</span>
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => {
                      setQuickFilter(null);
                      setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                    }}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 3. Status Multi-Select */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Status Multi-Select
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {INVOICE_STATUS_CONFIG.map((st) => {
                      const isSelected = filters.statuses.includes(st.id);
                      return (
                        <button
                          key={st.id}
                          type="button"
                          onClick={() => handleStatusToggle(st.id)}
                          className={`px-2.5 py-1 text-xs rounded-lg border transition-all cursor-pointer ${
                            isSelected
                              ? `${st.badgeBg} ${st.badgeText} border-blue-400 font-semibold ring-1 ring-blue-300`
                              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                          }`}
                        >
                          {st.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 4. Service Plan */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Service Plan
                  </label>
                  <select
                    value={filters.service}
                    onChange={(e) => setFilters((p) => ({ ...p, service: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All">All Services</option>
                    {dynamicAvailableServices.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 5. UPI ID / QR Filter */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5 flex items-center gap-1.5">
                    <FaQrcode className="text-purple-600 text-xs" />
                    <span>UPI ID / QR Account</span>
                  </label>
                  <select
                    value={filters.upiId}
                    onChange={(e) => setFilters((p) => ({ ...p, upiId: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  >
                    <option value="All">All UPI Accounts</option>
                    {dynamicAvailableUpiIds.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 6. RM (Relationship Manager) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Relationship Manager (RM)
                  </label>
                  <select
                    value={filters.rmName}
                    onChange={(e) => setFilters((p) => ({ ...p, rmName: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All">All RMs</option>
                    {dynamicAvailableRms.map((rm) => (
                      <option key={rm} value={rm}>
                        {rm}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 7. Client Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Client Type
                  </label>
                  <select
                    value={filters.clientType}
                    onChange={(e) => setFilters((p) => ({ ...p, clientType: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All">All Client Types</option>
                    <option value="New">New Client</option>
                    <option value="Renewal">Renewal</option>
                  </select>
                </div>

                {/* 8. Min Amount */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Min Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="e.g. 1000"
                    value={filters.minAmount}
                    onChange={(e) => setFilters((p) => ({ ...p, minAmount: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 9. Max Amount */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Max Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="e.g. 50000"
                    value={filters.maxAmount}
                    onChange={(e) => setFilters((p) => ({ ...p, maxAmount: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="overflow-x-auto min-h-[380px] touch-pan-x">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-100">
                  <th className="py-3 px-2 sm:px-3 text-center w-8 sm:w-10">
                    <input
                      type="checkbox"
                      checked={filteredInvoices.length > 0 && selectedInvoiceIds.size === filteredInvoices.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                      title="Select All Invoices"
                    />
                  </th>
                  <th className="py-3 px-2 sm:px-4 whitespace-nowrap">Invoice #</th>
                  <th className="py-3 px-2 sm:px-4">Buyer / Customer</th>
                  <th className="py-3 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">Status</th>
                  <th className="py-3 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">RM</th>
                  <th className="py-3 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">Mail</th>
                  <th className="py-3 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">Dated</th>
                  <th className="py-3 px-2 sm:px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((inv, index) => {
                    const isNearBottom = filteredInvoices.length > 3 && index >= filteredInvoices.length - 3;
                    const isSelected = selectedInvoiceIds.has(inv.id);
                    return (
                      <tr key={inv.id} className={`hover:bg-gray-50/80 transition-colors ${isSelected ? "bg-blue-50/40" : ""}`}>
                        <td className="py-3.5 px-2 sm:px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectInvoice(inv.id)}
                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                          />
                        </td>
                        <td className="py-3.5 px-2 sm:px-4 font-semibold text-blue-600 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              const url = getInvoicePdfUrl(inv);
                              console.log("[View PDF] URL:", url);
                              setViewingInvoice(inv);
                            }}
                            className="hover:underline font-mono text-xs sm:text-sm cursor-pointer"
                          >
                            {inv.id}
                          </button>
                        </td>
                        <td className="py-3.5 px-2 sm:px-4">
                          <div className="font-medium text-gray-800 text-xs sm:text-sm truncate max-w-[140px] sm:max-w-xs">
                            {inv.customerName}
                          </div>
                          <div className="text-[11px] sm:text-xs text-gray-400 truncate max-w-[140px] sm:max-w-xs">
                            {inv.phone !== "N/A" ? inv.phone : (inv.raw?.buyerCity || inv.raw?.buyerAddress || "")}
                          </div>
                          {inv.upiId && (
                            <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-purple-50 text-purple-700 border border-purple-200 truncate max-w-[180px]" title={`Payment QR UPI: ${inv.upiId}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
                              <span className="truncate">{inv.upiId}</span>
                            </div>
                          )}
                        </td>

                        {/* Status (Invoice Status) */}
                        <td className="py-3.5 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">
                          {renderStatusBadge(inv.status || "Created")}
                        </td>

                        {/* RM (Only Name) */}
                        <td className="py-3.5 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">
                          <span className="inline-flex items-center gap-1.5 font-medium text-gray-700 text-xs sm:text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                            <span className="truncate max-w-[110px]">{inv.rmName && inv.rmName !== "N/A" ? inv.rmName : "—"}</span>
                          </span>
                        </td>

                        {/* Mail (Email Address) */}
                        <td className="py-3.5 px-3 sm:px-4 whitespace-nowrap hidden md:table-cell">
                          {inv.mail && inv.mail !== "N/A" ? (
                            <a
                              href={`mailto:${inv.mail}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-[11px] sm:text-xs truncate max-w-[170px] inline-block"
                              title={`Send email to ${inv.mail}`}
                            >
                              {inv.mail}
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Dated */}
                        <td className="py-3.5 px-3 sm:px-4 text-gray-500 text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">
                          {inv.date}
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-2 sm:px-4 text-center whitespace-nowrap relative">
                          <button
                            type="button"
                            onClick={() => setOpenMenuId(openMenuId === inv.id ? null : inv.id)}
                            className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            title="Invoice Details & Actions"
                          >
                            <FaEllipsisV size={14} />
                          </button>

                          {openMenuId === inv.id && (
                            <>
                              <div className="fixed inset-0 z-30" onClick={() => setOpenMenuId(null)} />
                              <div
                                className={`absolute right-0 sm:right-2 ${
                                  isNearBottom ? "bottom-full mb-2" : "top-full mt-1"
                                } w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-40 animate-fadeIn text-left divide-y divide-gray-100`}
                              >
                                {/* 1. Header: Invoice # */}
                                <div className="p-3.5 bg-gray-50/80 rounded-t-xl">
                                  <div className="mb-1.5">
                                    <span className="font-mono font-bold text-xs text-blue-600 truncate">
                                      {inv.id}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-gray-500 flex items-center justify-between">
                                    <span>Dated: {inv.date}</span>
                                    <span>Due: {inv.dueDate || "Immediate"}</span>
                                  </div>
                                </div>

                                {/* Client & RM Overview */}
                                <div className="p-3 bg-white space-y-2 text-xs">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-gray-500 text-[11px]">Status:</span>
                                    {renderStatusBadge(inv.status || "Created")}
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-gray-500 text-[11px]">Relationship Mgr:</span>
                                    <span className="font-semibold text-gray-800">{inv.rmName && inv.rmName !== "N/A" ? inv.rmName : "—"}</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-gray-500 text-[11px]">Mail Address:</span>
                                    {inv.mail && inv.mail !== "N/A" ? (
                                      <a
                                        href={`mailto:${inv.mail}`}
                                        className="font-mono text-[11px] text-blue-600 hover:underline truncate max-w-[160px]"
                                        title={`Send email to ${inv.mail}`}
                                      >
                                        {inv.mail}
                                      </a>
                                    ) : (
                                      <span className="text-gray-400 text-[11px]">—</span>
                                    )}
                                  </div>
                                </div>

                                {/* 2. Service Details & Period */}
                                <div className="p-3.5 space-y-1.5 text-xs">
                                  <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                      Service Description
                                    </div>
                                    <div className="font-medium text-gray-800 break-words mt-0.5">
                                      {inv.service}
                                    </div>
                                  </div>
                                  {(inv.serviceStartDate || inv.serviceEndDate) && (
                                    <div className="text-[11px] text-gray-600 bg-blue-50/60 px-2 py-1 rounded border border-blue-100/80 mt-1">
                                      <span className="font-semibold text-blue-700">Period: </span>
                                      <span>
                                        {inv.serviceStartDate || "N/A"} to {inv.serviceEndDate || "N/A"}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* 3. Financial Summary */}
                                <div className="p-3.5 bg-gray-50/50 space-y-1.5 text-xs font-mono">
                                  <div className="flex justify-between items-center text-gray-600">
                                    <span className="font-sans text-[11px] text-gray-500">Taxable Value:</span>
                                    <span>
                                      ₹{Number(inv.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center text-gray-600">
                                    <span className="font-sans text-[11px] text-gray-500">Total GST:</span>
                                    <span>
                                      ₹{Number(inv.gst || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center font-bold text-blue-900 pt-1.5 border-t border-gray-200 text-sm">
                                    <span className="font-sans text-xs text-gray-700">Total (incl. GST):</span>
                                    <span>
                                      ₹{Number(inv.total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </div>

                                {/* 4. Action Buttons */}
                                <div className="py-1.5">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const url = getInvoicePdfUrl(inv);
                                      console.log("[View PDF] URL:", url);
                                      setViewingInvoice(inv);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaEye className="text-gray-400" />
                                    <span>View</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => setOpenMenuId(null)}
                                    className="w-full px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaPaperPlane className="text-gray-400" />
                                    <span>Send to Email</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleDownloadSinglePdf(inv);
                                    }}
                                    disabled={generatingPdfInvoiceId === (inv.id || "invoice")}
                                    className="w-full px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2.5 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                                  >
                                    {generatingPdfInvoiceId === (inv.id || "invoice") ? (
                                      <FaSpinner className="text-blue-500 animate-spin" />
                                    ) : (
                                      <FaFilePdf className="text-emerald-500" />
                                    )}
                                    <span>
                                      {generatingPdfInvoiceId === (inv.id || "invoice")
                                        ? "Generating PDF..."
                                        : "Download PDF"}
                                    </span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleCancelInvoice(inv);
                                      setOpenMenuId(null);
                                    }}
                                    className="w-full px-3.5 py-2 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors cursor-pointer"
                                  >
                                    <FaTimesCircle className="text-red-400" />
                                    <span>Cancel Invoice</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-400">
                      <FaFileInvoice className="mx-auto text-4xl mb-3 text-gray-300" />
                      <p className="font-medium text-gray-500">No invoices found.</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Click "Create New Invoice" or "Bulk Upload" to import statutory invoices.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {selectedInvoiceIds.size > 0 && (
          <div className="fixed bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900/95 text-white px-3 py-2 sm:px-5 sm:py-3 rounded-2xl shadow-2xl backdrop-blur-md flex flex-wrap items-center justify-center gap-1.5 sm:gap-3 border border-gray-700/60 animate-slideUp text-xs sm:text-sm max-w-[95vw] w-max">
            <span className="font-semibold text-blue-300 whitespace-nowrap">
              {selectedInvoiceIds.size} <span className="hidden sm:inline">{selectedInvoiceIds.size === 1 ? "invoice" : "invoices"}</span> sel.
            </span>
            <div className="h-4 w-px bg-gray-700 mx-0.5 hidden sm:block" />
            <button
              type="button"
              onClick={() => {
                const selectedList = invoices.filter((i) => selectedInvoiceIds.has(i.id));
                handleDownloadInvoicesAsZip(selectedList);
              }}
              disabled={isGeneratingZip}
              className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-white font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              title="Download all selected invoices as individual named PDFs inside a ZIP archive"
            >
              {isGeneratingZip ? (
                <FaSpinner className="animate-spin" size={12} />
              ) : (
                <FaFileArchive size={12} />
              )}
              <span className="hidden sm:inline">Download ZIP</span>
              <span className="sm:hidden">ZIP</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const selectedList = invoices.filter((i) => selectedInvoiceIds.has(i.id));
                handleExportInvoicesToExcel(selectedList, `Selected_Invoices_${selectedList.length}.xlsx`);
              }}
              className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              <FaFileExcel size={12} />
              <span className="hidden sm:inline">Export Excel</span>
              <span className="sm:hidden">Excel</span>
            </button>
            <button
              type="button"
              onClick={() => setSelectedInvoiceIds(new Set())}
              className="px-2 py-1.5 text-gray-400 hover:text-white rounded-lg cursor-pointer whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-xs overflow-y-auto">
          <div
            className={`bg-white rounded-2xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn my-6 flex flex-col ${
              parsedUploadRows.length > 0 ? "max-w-4xl max-h-[90vh]" : "max-w-xl"
            }`}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <FaFileExcel className="text-emerald-600 text-xl" />
                <div>
                  <h3 className="font-bold text-gray-800 text-base">
                    {parsedUploadRows.length > 0 ? "Bulk Invoices Preview & Import" : "Bulk Upload Client Invoices"}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {parsedUploadRows.length > 0
                      ? `${parsedUploadRows.length} valid invoice rows detected from file`
                      : "Upload client excel sheet (.xlsx) to bulk generate statutory invoices"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isUploading || isParsingFile}
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setParsedUploadRows([]);
                  setRawWorkbookData([]);
                }}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaTimes size={16} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {parsedUploadRows.length === 0 ? (
                <>
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !isParsingFile && fileInputRef.current && fileInputRef.current.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      isParsingFile
                        ? "border-emerald-500 bg-emerald-50/50 cursor-wait"
                        : isDragging
                        ? "border-emerald-500 bg-emerald-50/40 cursor-pointer"
                        : "border-gray-300 hover:border-emerald-400 bg-gray-50/50 cursor-pointer"
                    }`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      disabled={isParsingFile}
                    />
                    {isParsingFile ? (
                      <div className="flex flex-col items-center justify-center py-2">
                        <span className="w-10 h-10 border-3 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-3 shrink-0"></span>
                        <p className="text-base font-semibold text-emerald-800">Processing Excel File...</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Reading invoice rows and calculating tax values... please wait
                        </p>
                      </div>
                    ) : (
                      <>
                        <FaUpload className="mx-auto text-4xl text-emerald-600 mb-3" />
                        <p className="text-base font-semibold text-gray-800">Click or drag Excel file here</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Supports client invoice format (.xlsx, .xls, .csv) with automatic state & tax calculations
                        </p>
                      </>
                    )}
                  </div>

                  {/* Field Guide Specification Callout */}
                  <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-xl p-4 text-xs text-emerald-950 space-y-2.5">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-900 text-xs">
                      <FaInfoCircle className="text-emerald-600 shrink-0" />
                      <span>Excel Format & Field Specifications:</span>
                    </div>

                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5 space-y-1.5">
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded text-[10px] shrink-0 tracking-wide uppercase">
                          * 7 Mandatory Fields
                        </span>
                        <span className="text-[11px] text-gray-700 font-medium">
                          <strong>Client Name</strong>, <strong>Mobile</strong>, <strong>Email</strong>, <strong>Pan</strong>, <strong>City</strong>, <strong>Date</strong>, <strong>Payment</strong>
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 pl-1">
                        Only these 7 fields from your client excel are strictly required to generate compliant statutory invoices.
                      </p>
                    </div>

                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5 space-y-1.5">
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded text-[10px] shrink-0 tracking-wide uppercase">
                          Auto-Calculated
                        </span>
                        <span className="text-[11px] text-gray-600">
                          <strong>State & Code</strong> (from City), <strong>18% GST</strong> (Intra MP CGST+SGST vs Inter IGST), <strong>Taxable Base</strong>, <strong>Service Dates</strong> (+30d), <strong>Amount in Words</strong>, and <strong>Sequential Invoice No</strong>.
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 pl-1">
                        Optional overrides (Bank, Address, GSTIN, Delivery Note, Dispatch Doc No) can be provided or left blank.
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-between text-xs text-gray-500 border-t border-gray-100">
                    <span className="text-gray-500">Need the full template with field guide?</span>
                    <button
                      type="button"
                      onClick={handleDownloadSample}
                      className="font-semibold text-emerald-700 hover:text-emerald-800 hover:underline flex items-center gap-1.5 cursor-pointer bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200/60"
                    >
                      <FaDownload size={11} className="text-emerald-600" />
                      <span>Download Sample Excel</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900">
                    <div className="flex items-center gap-2">
                      <FaFileExcel className="text-emerald-600 text-base shrink-0" />
                      <span className="font-semibold truncate max-w-xs">{selectedFile?.name}</span>
                      <span className="bg-emerald-200/70 text-emerald-800 font-bold px-2 py-0.5 rounded-full text-[10px]">
                        {parsedUploadRows.length} Invoices
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setParsedUploadRows([]);
                        setRawWorkbookData([]);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="text-xs font-semibold text-emerald-700 hover:text-red-600 cursor-pointer"
                    >
                      Change File
                    </button>
                  </div>

                  <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl text-xs space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-gray-200">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isBulkGstInvoice}
                          onChange={(e) => handleBulkGstToggle(e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                        />
                        <span className="font-bold text-gray-800 text-sm">
                          GST Invoice (Calculate 18% GST)
                        </span>
                      </label>
                      <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${isBulkGstInvoice ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {isBulkGstInvoice ? "Standard GST Enabled" : "Without Tax (0% GST)"}
                      </span>
                    </div>

                    {isBulkGstInvoice ? (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2">Tax Calculation Mode:</div>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="taxMode"
                              value="inclusive"
                              checked={taxCalculationMode === "inclusive"}
                              onChange={() => handleTaxModeChange("inclusive")}
                              className="text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-gray-800 font-medium">
                              Payment is <strong>Inclusive of 18% GST</strong> (e.g. ₹11,800 total = ₹10,000 base + ₹1,800 tax)
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="taxMode"
                              value="exclusive"
                              checked={taxCalculationMode === "exclusive"}
                              onChange={() => handleTaxModeChange("exclusive")}
                              className="text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-gray-800 font-medium">
                              Payment is <strong>Exclusive of GST</strong> (Base Amount + 18% added)
                            </span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700 bg-amber-50/80 p-2 rounded-lg border border-amber-200/60">
                        GST calculation is disabled. All {parsedUploadRows.length} invoices will be created without tax (0% GST rate, Gross = Base Taxable).
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100">
                      <div className="text-[10px] uppercase font-semibold text-blue-600">Invoices Count</div>
                      <div className="text-xl font-bold text-blue-900 mt-0.5">{parsedUploadRows.length}</div>
                    </div>
                    <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                      <div className="text-[10px] uppercase font-semibold text-emerald-600">Total Gross (₹)</div>
                      <div className="text-xl font-bold text-emerald-900 mt-0.5 font-mono">
                        ₹{Math.round(parsedUploadRows.reduce((s, r) => s + r.grandTotal, 0)).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50/60 rounded-xl border border-purple-100">
                      <div className="text-[10px] uppercase font-semibold text-purple-600">Base Taxable (₹)</div>
                      <div className="text-xl font-bold text-purple-900 mt-0.5 font-mono">
                        ₹{Math.round(parsedUploadRows.reduce((s, r) => s + r.taxable, 0)).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100">
                      <div className="text-[10px] uppercase font-semibold text-indigo-600">Total GST (₹)</div>
                      <div className="text-xl font-bold text-indigo-900 mt-0.5 font-mono">
                        ₹{Math.round(parsedUploadRows.reduce((s, r) => s + r.totalTax, 0)).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto max-h-64 text-xs">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-100/80 sticky top-0 border-b border-gray-200 text-gray-600 font-semibold uppercase text-[10px]">
                          <tr>
                            <th className="py-2.5 px-3">#</th>
                            <th className="py-2.5 px-3">Customer Name</th>
                            <th className="py-2.5 px-3">City / State</th>
                            <th className="py-2.5 px-3">Service & Period</th>
                            <th className="py-2.5 px-3 text-right">Taxable (₹)</th>
                            <th className="py-2.5 px-3 text-right">GST (₹)</th>
                            <th className="py-2.5 px-3 text-right">Total (₹)</th>
                            <th className="py-2.5 px-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {parsedUploadRows.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50/80 transition-colors">
                              <td className="py-2 px-3 text-gray-400 font-mono">{r.sl}</td>
                              <td className="py-2 px-3">
                                <div className="font-semibold text-gray-800">{r.clientName}</div>
                                <div className="text-[10px] text-gray-400">{r.phone || r.email || r.pan}</div>
                                {r.upiId && (
                                  <div className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-mono text-purple-700 bg-purple-50 px-1 py-0.5 rounded border border-purple-200 truncate max-w-[160px]" title={`UPI: ${r.upiId}`}>
                                    <span className="w-1 h-1 rounded-full bg-purple-500 shrink-0"></span>
                                    <span className="truncate">{r.upiId}</span>
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <div className="text-gray-700">{r.city || "—"}</div>
                                <div className="text-[10px] text-blue-600 font-medium">
                                  {r.stateInfo.state} ({r.stateInfo.code})
                                </div>
                              </td>
                              <td className="py-2 px-3 max-w-xs">
                                <div className="font-medium text-gray-700 truncate">{r.serviceName}</div>
                                {r.serviceStartDateVal && (
                                  <div className="text-[10px] text-gray-400">
                                    {r.serviceStartDateVal} to {r.serviceEndDateVal || "N/A"}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-gray-600">
                                ₹{r.taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-indigo-600">
                                ₹{r.totalTax.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-3 text-right font-mono font-bold text-gray-900">
                                ₹{r.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                  {r.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
              <button
                type="button"
                disabled={isUploading}
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setParsedUploadRows([]);
                  setRawWorkbookData([]);
                }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              {parsedUploadRows.length > 0 && (
                <button
                  type="button"
                  onClick={handleConfirmBulkUpload}
                  disabled={isUploading}
                  className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer shadow-sm transition-all"
                >
                  {isUploading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0"></span>
                      <span>Uploading & Importing {parsedUploadRows.length} Invoices...</span>
                    </>
                  ) : (
                    <>
                      <FaCheck className="text-xs" />
                      <span>Confirm & Upload {parsedUploadRows.length} Invoices</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn my-4 sm:my-8 flex flex-col max-h-[92vh]">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <FaPlus className="text-blue-600 text-base sm:text-lg" />
                <div>
                  <h3 className="font-bold text-gray-800 text-base sm:text-lg">Create New Invoice</h3>
                  <p className="text-xs text-gray-500">
                    Next Invoice No: <span className="font-mono font-bold text-blue-600">{nextInvoiceNo}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isCreatingInvoice}
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateInvoiceSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              <div>
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 pb-1 border-b border-gray-200">
                  1. Buyer (Bill To) Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      Customer / Company Name *
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleFormChange}
                      required
                      placeholder="e.g. KRISHNA KUMAR YADAV"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      BuyerPan
                    </label>
                    <input
                      type="text"
                      name="panno"
                      value={formData.panno}
                      onChange={handleFormChange}
                      placeholder="e.g. ABCDE1234F"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="e.g. krishna.yadav@example.com"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="e.g. +91-9893022343"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      Billing Address *
                    </label>
                    <input
                      type="text"
                      name="buyerAddress"
                      value={formData.buyerAddress}
                      onChange={handleFormChange}
                      required
                      placeholder="e.g. 297, DEV NAGAR, OLD PALASIA JAIN MANDIR, RAU, INDORE"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      Buyer State Name *
                    </label>
                    <select
                      name="buyerStateName"
                      value={formData.buyerStateName}
                      onChange={handleFormChange}
                      required
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES_LIST.map((st) => (
                        <option key={st.code} value={st.name}>
                          {st.name} ({st.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                      State Code
                    </label>
                    <input
                      type="text"
                      name="buyerStateCode"
                      value={formData.buyerStateCode}
                      readOnly
                      placeholder="Auto-loaded from state"
                      className="w-full px-3.5 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 font-mono text-gray-700 cursor-not-allowed select-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 pb-1.5 mb-3 border-b border-gray-200">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    2. Services & Line Items (Single/Multiple Services)
                  </h4>
                  <label className="flex items-center gap-2 cursor-pointer select-none bg-blue-50/70 border border-blue-200/80 px-2.5 py-1 rounded-lg">
                    <input
                      type="checkbox"
                      name="isGstInvoice"
                      checked={formData.isGstInvoice}
                      onChange={handleFormChange}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-bold text-blue-900">
                      GST Invoice {formData.isGstInvoice ? "(18% GST Enabled)" : "(Without Tax / 0% GST)"}
                    </span>
                  </label>
                </div>

                {formData.isGstInvoice && (
                  <div className="mb-3 p-2.5 bg-blue-50/40 border border-blue-200/60 rounded-xl flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-gray-700 font-semibold">Standard GST Rate (%):</span>
                    <select
                      name="taxRatePercent"
                      value={formData.taxRatePercent}
                      onChange={handleFormChange}
                      className="text-xs bg-white border border-gray-300 rounded px-2.5 py-1 font-semibold text-gray-800 cursor-pointer outline-none focus:border-blue-500"
                    >
                      <option value="18">18% (Standard GST)</option>
                    </select>
                  </div>
                )}

                <div className="space-y-3">
                  {formData.items.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="p-3.5 bg-gray-50/70 border border-gray-200 rounded-xl space-y-3 transition-all hover:border-gray-300 relative"
                    >
                      <div className="flex items-center justify-between pb-1 border-b border-gray-200/60">
                        <span className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-blue-600 text-white inline-flex items-center justify-center text-[10px] font-bold">
                            {idx + 1}
                          </span>
                          Service Item #{idx + 1}
                        </span>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(idx)}
                            className="text-xs text-red-600 hover:text-red-700 px-2 py-0.5 rounded hover:bg-red-50 flex items-center gap-1 cursor-pointer transition-colors"
                            title="Remove this service"
                          >
                            <FaTrash size={11} />
                            <span>Remove</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                            Service Description *
                          </label>
                          <input
                            type="text"
                            value={item.service}
                            onChange={(e) => handleItemChange(idx, "service", e.target.value)}
                            required
                            placeholder="e.g. Equity Premium Package / Research Service Monthly"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                            HSN / SAC Code
                          </label>
                          <input
                            type="text"
                            value={item.hsnSac}
                            onChange={(e) => handleItemChange(idx, "hsnSac", e.target.value)}
                            placeholder="998222"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={item.serviceStartDate}
                            onChange={(e) => handleItemChange(idx, "serviceStartDate", e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={item.serviceEndDate}
                            onChange={(e) => handleItemChange(idx, "serviceEndDate", e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-2 sm:col-span-1">
                          <div className="col-span-1">
                            <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                              Qty
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono bg-white text-center"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-[11px] font-semibold text-gray-600 uppercase mb-1">
                              Price / Unit (₹) *
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => handleItemChange(idx, "rate", e.target.value)}
                              required
                              placeholder="10000"
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono bg-white"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200/60">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">
                            Discount (₹)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.discountAmount || ""}
                            onChange={(e) => handleItemChange(idx, "discountAmount", e.target.value)}
                            placeholder="0.00"
                            className="w-full px-2.5 py-1 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 font-mono bg-white"
                          />
                        </div>
                        <div className="flex items-center justify-end text-xs text-gray-600 font-mono pt-2">
                          <span>Item Net: </span>
                          <span className="font-bold text-gray-900 ml-1.5 text-sm">
                            ₹{Math.max(
                              0,
                              (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 1) -
                                (parseFloat(item.discountAmount) || 0)
                            ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="w-full py-2.5 px-4 border-2 border-dashed border-blue-300 hover:border-blue-500 bg-blue-50/40 hover:bg-blue-50 text-blue-700 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all"
                  >
                    <FaPlus size={12} />
                    <span> Add Another Service (Multiple Services for Single User)</span>
                  </button>
                </div>
              </div>

              {formTotals.baseTaxable > 0 && (
                <div className="p-4 bg-gradient-to-br from-blue-50/80 to-indigo-50/80 border border-blue-200 rounded-xl space-y-1.5 text-xs text-gray-700 font-mono shadow-xs">
                  <div className="flex justify-between">
                    <span>Base Taxable Value ({formData.items.length} {formData.items.length === 1 ? "Service" : "Services"}):</span>
                    <span className="font-semibold">
                      ₹{formTotals.baseTaxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {formTotals.totalDiscount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Total Discount:</span>
                      <span>
                        - ₹{formTotals.totalDiscount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {formData.isGstInvoice ? (
                    formTotals.isIntraState ? (
                      <>
                        <div className="flex justify-between text-blue-700">
                          <span>Central Tax CGST ({formTotals.taxRate / 2}%):</span>
                          <span>
                            ₹{formTotals.cgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-700">
                          <span>State Tax SGST ({formTotals.taxRate / 2}%):</span>
                          <span>
                            ₹{formTotals.sgst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-purple-700">
                        <span>Integrated Tax IGST ({formTotals.taxRate}%):</span>
                        <span>
                          ₹{formTotals.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    )
                  ) : (
                    <div className="flex justify-between text-gray-500 italic">
                      <span>Tax (Without GST):</span>
                      <span>₹ 0.00 (0%)</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold text-blue-900 pt-2 border-t border-blue-200">
                    <span>Total Invoice Amount:</span>
                    <span>
                      ₹{formTotals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  disabled={isCreatingInvoice}
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-center"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingInvoice}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg shadow-sm cursor-pointer text-center transition-all"
                >
                  {isCreatingInvoice ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0"></span>
                      <span>Creating Invoice...</span>
                    </>
                  ) : (
                    <span>Generate Statutory Tax Invoice</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs overflow-hidden">
          <div className="bg-white rounded-2xl max-w-5xl w-full shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn my-2 sm:my-4 flex flex-col h-[94vh] max-h-[96vh]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-6 py-2.5 sm:py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0 gap-2 print:hidden">
              <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FaFileInvoice className="text-blue-600 text-lg sm:text-xl shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-800 text-sm sm:text-base truncate">
                      Tax Invoice — {viewingInvoice.id}
                    </h3>
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      {viewingInvoice.raw?.sellerName || "EquityReaserchMart Consultants LLP"} • Status:{" "}
                      <strong>{viewingInvoice.status}</strong>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingInvoice(null)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer sm:hidden shrink-0"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadSinglePdf(viewingInvoice)}
                  disabled={generatingPdfInvoiceId === (viewingInvoice?.id || "invoice")}
                  className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-xs transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-wait"
                  title="Download this invoice as a PDF file"
                >
                  {generatingPdfInvoiceId === (viewingInvoice?.id || "invoice") ? (
                    <FaSpinner className="text-xs animate-spin" />
                  ) : (
                    <FaDownload className="text-xs" />
                  )}
                  <span>
                    {generatingPdfInvoiceId === (viewingInvoice?.id || "invoice")
                      ? "Downloading..."
                      : "Download"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setViewingInvoice(null)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer hidden sm:block"
                >
                  <FaTimes size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 w-full bg-gray-100 p-2 sm:p-3 overflow-hidden flex flex-col min-h-0">
              <iframe
                src={`${getInvoicePdfUrl(viewingInvoice)}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                title={`Invoice Preview ${viewingInvoice.id}`}
                className="w-full flex-1 rounded-xl border border-gray-300 bg-white shadow-sm"
                style={{ width: "100%", height: "100%", minHeight: "calc(94vh - 85px)" }}
              />
            </div>
          </div>
        </div>
      )}

      {bulkPrintInvoices && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-gray-200 overflow-hidden animate-fadeIn my-6 flex flex-col max-h-[95vh]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-6 py-2.5 sm:py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0 gap-2 print:hidden">
              <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <FaFileInvoice className="text-base" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-800 text-sm sm:text-base truncate">
                      Bulk Invoices ({bulkPrintInvoices.length})
                    </h3>
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate hidden sm:block">
                      Combined multi-page statutory invoices ready for browser print or saving as PDF
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkPrintInvoices(null)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer sm:hidden shrink-0"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                <div className="flex items-center gap-1 sm:gap-1.5 bg-gray-100 px-2 py-1 rounded-lg border border-gray-200">
                  <span className="text-[11px] font-medium text-gray-600 hidden md:inline">Template:</span>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="text-[11px] sm:text-xs bg-white border border-gray-300 rounded px-1.5 sm:px-2 py-0.5 font-semibold text-gray-800 cursor-pointer outline-none focus:border-blue-500"
                  >
                    <option value="equity_mart">Equity Research Mart (Official Standard)</option>
                    <option value="tally">Statutory Tally Format (Alternative)</option>
                  </select>
                </div>

                <label
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  title="Upload / Change signature image for all invoices"
                >
                  <FaPenNib className="text-gray-500 text-xs" />
                  <span className="hidden sm:inline">{signatureUrl ? "Change Sign" : "Sign All"}</span>
                  <span className="sm:hidden">{signatureUrl ? "Sign" : "Sign"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSignatureUpload}
                    className="hidden"
                  />
                </label>

                {signatureUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveSignature}
                    className="text-xs text-red-600 hover:text-red-700 px-1.5 sm:px-2 py-1 rounded hover:bg-red-50 cursor-pointer"
                    title="Remove signature"
                  >
                    Clear Sign
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleDownloadInvoicesAsZip(bulkPrintInvoices)}
                  disabled={isGeneratingZip}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  title="Download all invoices as individual PDFs inside a ZIP archive"
                >
                  {isGeneratingZip ? (
                    <FaSpinner className="animate-spin text-xs" />
                  ) : (
                    <FaFileArchive className="text-purple-600 text-xs" />
                  )}
                  <span className="hidden sm:inline">Download ZIP</span>
                  <span className="sm:hidden">ZIP</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleExportInvoicesToExcel(
                      bulkPrintInvoices,
                      `Bulk_Invoices_${bulkPrintInvoices.length}.xlsx`
                    )
                  }
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors cursor-pointer"
                >
                  <FaFileExcel className="text-emerald-600 text-xs" />
                  <span className="hidden sm:inline">Export Excel</span>
                  <span className="sm:hidden">Excel</span>
                </button>


                <button
                  type="button"
                  onClick={() => setBulkPrintInvoices(null)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer hidden sm:block"
                >
                  <FaTimes size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-2 sm:p-8 bg-gray-100 print:bg-white print:p-0 print:overflow-visible">
              <div id="bulk-print-root" className="min-w-[680px] sm:min-w-0 max-w-[820px] mx-auto print:min-w-0 print:w-full print:max-w-none space-y-6 print:space-y-0">
                {bulkPrintInvoices.map((inv, idx) => (
                  <div key={inv.id || idx} className="invoice-page bg-white shadow-sm sm:shadow-md print:shadow-none">
                    <StatutoryInvoiceDoc
                      invoice={inv}
                      entityProfile={entityProfile}
                      template={selectedTemplate}
                      signatureUrl={signatureUrl}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isGeneratingZip && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-100 text-center space-y-4 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mx-auto text-xl">
              <FaFileArchive className="animate-pulse text-2xl" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-base">Packaging Invoices ZIP</h3>
              <p className="text-xs text-gray-600 mt-1 font-medium">{zipProgress || "Preparing files..."}</p>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div className="bg-purple-600 h-full w-full animate-pulse rounded-full" />
            </div>
            <p className="text-[11px] text-gray-400">Please wait while PDFs are generated and zipped...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePanel;
